import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Crown, RefreshCw, Server, HardDrive, Activity,
  Settings2, ScrollText, AlertTriangle,
  Trash2, Save, Search,
  Zap, Shield, Database, Radio, Power,
  TrendingUp, BarChart3, Users, Globe, Clock,
  Check, X, Play, Info, Lock, Wifi, Megaphone, Image, Link, ToggleLeft,
  ExternalLink, Calendar, ChevronDown, ChevronUp, DollarSign, Copy,
  CreditCard, Mail, Download,
} from 'lucide-react';
import { Spinner } from '../components/ui';
import {
  getAdminSystemOverview,
  getAdminConfig,
  getAdminStorageStats, getAdminEventLogs,
  purgeOldStorage, triggerHealthCheck,
  listBots, getAdConfig, saveAdConfig,
  getAdRequests, approveAdRequest, rejectAdRequest, deleteAdRequest, uploadAdImage,
  adminListUsers, adminResetPassword, adminChangeTier, adminSuspendUser, adminDeleteUser,
  getBusinessKpis, getTreasuryBalances, getWorkerStatus, getMgmtAuditLog, getReferralsDashboard,
  getPaidChannelFlags, getPaidChannelPending, getPaidChannelFailedPayouts,
  getPaidChannelTreasuryStatus, getPaidChannelActiveChannels,
  retryPaidChannelPayout, togglePaidChannelFlag, managePaidChannelAllowlist,
  getPaidChannelDetail, approvePaidChannelApp, rejectPaidChannelApp,
  getAdminTeamRoles, getAdminTeamMembers, updateAdminTeamMemberRoles, revokeAdminTeamMember,
  getAdminTeamInvites, createAdminTeamInvite, revokeAdminTeamInvite,
} from '../services/api';
import { useAuth, usePermission } from '../hooks/useAuth';
import { StepUpProvider, useStepUpGate } from '../hooks/useStepUpGate';
import {
  getFilteredAuditLog, getAuditLogCsvUrl,
  getAdminSessions, revokeAdminSession,
  listApprovals, decideApproval,
  getCurrentSessionJti,
} from '../services/api';

// ─── Shared helpers ───────────────────────────────────────────────────────────

function formatUptime(secs) {
  if (!secs) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${h}h ${m}m ${s}s`;
}

function formatBytes(kb) {
  if (kb < 1024) return `${kb} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

// ─── UI primitives ────────────────────────────────────────────────────────────

function Toggle({ enabled, onChange }) {
  return (
    <button onClick={() => onChange(!enabled)}
      className={`relative inline-flex w-11 h-6 items-center rounded-full transition-colors focus:outline-none ${enabled ? 'bg-accent-primary' : 'bg-dark-600'}`}>
      <span className={`inline-block w-4 h-4 rounded-full bg-white shadow transform transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

function SectionCard({ title, subtitle, icon: Icon, action, children, danger }) {
  return (
    <div className={`bg-dark-800 rounded-2xl p-6 border ${danger ? 'border-red-500/20' : 'border-dark-700/50'}`}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${danger ? 'bg-red-500/10 text-red-400' : 'bg-accent-primary/10 text-accent-primary'}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div>
            <h3 className={`font-semibold text-sm ${danger ? 'text-red-400' : 'text-white'}`}>{title}</h3>
            {subtitle && <p className="text-gray-500 text-xs mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function StatHero({ label, value, icon: Icon, color = 'text-accent-primary', bg = 'bg-accent-primary/10', sub, pulse }) {
  return (
    <div className="bg-dark-800 rounded-2xl p-5 border border-dark-700/50 flex items-start gap-4">
      <div className={`w-11 h-11 rounded-xl ${bg} ${color} flex items-center justify-center flex-shrink-0 relative`}>
        <Icon className="w-5 h-5" />
        {pulse && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-dark-800" style={{ boxShadow: '0 0 6px #34d399' }} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-gray-500 text-xs">{label}</p>
        <p className={`text-2xl font-bold mt-0.5 ${color}`}>{value}</p>
        {sub && <p className="text-gray-600 text-xs mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function StatusDot({ ok, label }) {
  return (
    <div className="flex items-center gap-3 bg-dark-700/40 rounded-xl px-4 py-3">
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${ok ? 'bg-emerald-400' : 'bg-red-400'}`}
        style={ok ? { boxShadow: '0 0 5px #34d399' } : undefined} />
      <span className="text-gray-300 text-sm flex-1">{label}</span>
      <span className={`text-xs font-medium ${ok ? 'text-emerald-400' : 'text-red-400'}`}>{ok ? 'OK' : 'ERR'}</span>
    </div>
  );
}

function Toast({ message, onClose }) {
  if (!message) return null;
  const err = message.type === 'error';
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl border text-sm font-medium animate-fade-in ${err ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'}`}>
      {err ? <AlertTriangle className="w-4 h-4" /> : <Check className="w-4 h-4" />}
      {message.text}
      <button onClick={onClose} className="ml-1 text-gray-500 hover:text-gray-300"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

// ─── Tab: Command Center ──────────────────────────────────────────────────────

// Derives a 0-100 platform health score and a scaling recommendation
function computePlatformHealth(overview, bots, storageStats) {
  let score = 100;
  const issues = [];
  const warnings = [];

  if (!overview) return { score: 0, label: 'Unknown', color: 'text-gray-400', issues: ['Cannot reach backend API'], warnings: [], recommendation: 'check' };

  const totalBots  = overview.botCount ?? 0;
  const totalFiles = overview.storageStats?.totalFiles ?? 0;
  const uptime     = overview.uptime ?? 0;
  const pausedBots = bots.filter(b => b.status === 'paused').length;

  if (uptime < 300)              { score -= 20; issues.push('Backend recently restarted (uptime < 5 min)'); }
  if (pausedBots === totalBots && totalBots > 0) { score -= 15; warnings.push('All bots are paused'); }
  if (totalFiles > 5000)         { score -= 10; warnings.push(`Storage has ${totalFiles} records — consider purging old data`); }
  if (totalFiles > 10000)        { score -= 15; issues.push('Storage nearing high volume — schedule purge'); }

  const storageKb = storageStats?.totalSizeKb ?? 0;
  if (storageKb > 50000)         { score -= 10; warnings.push('Storage exceeding 50 MB — monitor growth'); }

  score = Math.max(0, score);
  const label = score >= 90 ? 'Excellent' : score >= 70 ? 'Good' : score >= 50 ? 'Fair' : 'Needs Attention';
  const color = score >= 90 ? 'text-emerald-400' : score >= 70 ? 'text-accent-primary' : score >= 50 ? 'text-amber-400' : 'text-red-400';
  const barColor = score >= 90 ? 'bg-emerald-400' : score >= 70 ? 'bg-accent-primary' : score >= 50 ? 'bg-amber-400' : 'bg-red-400';

  // Scaling recommendation
  let recommendation = 'healthy';
  if (score < 50)                recommendation = 'critical';
  else if (score < 70 || totalBots > 50 || totalFiles > 8000) recommendation = 'monitor';

  return { score, label, color, barColor, issues, warnings, recommendation };
}

// Network label mapping (no provider names)
const NETWORK_LABELS = {
  ethereum_mainnet: 'Ethereum',
  polygon_mainnet:  'Polygon',
  arbitrum_mainnet: 'Arbitrum',
  base_mainnet:     'Base',
};

function CommandCenterTab({ onToast }) {
  const [overview,     setOverview]     = useState(null);
  const [bots,         setBots]         = useState([]);
  const [storageStats, setStorageStats] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [triggering,   setTriggering]   = useState(null);
  const [botSearch,    setBotSearch]    = useState('');
  const [lastRefresh,  setLastRefresh]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, bl, ss] = await Promise.all([
        getAdminSystemOverview().catch(() => null),
        listBots().catch(() => []),
        getAdminStorageStats().catch(() => null),
      ]);
      setOverview(ov);
      setBots(bl || []);
      setStorageStats(ss);
      setLastRefresh(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Orchestrator button removed — the /api/orchestrator/trigger endpoint
  // was retired with the MCN volume-keeper. Re-add if a new orchestrator
  // ships. Health check still works.
  const trigger = async () => {
    setTriggering('health');
    try {
      await triggerHealthCheck();
      onToast({ type: 'success', text: 'Platform health check completed' });
      load();
    } catch (err) { onToast({ type: 'error', text: err.message }); }
    finally { setTriggering(null); }
  };

  const isLive     = overview?.simulationMode === 'false' && overview?.dexSimulationMode === 'false';
  const totalBots  = overview?.botCount ?? 0;
  const activeBots = bots.filter(b => b.status !== 'paused').length;
  const pausedBots = bots.filter(b => b.status === 'paused').length;

  // Network distribution from bot list
  const networkDist = bots.reduce((acc, b) => {
    const net = NETWORK_LABELS[b.network] || b.network || 'Unknown';
    acc[net] = (acc[net] || 0) + 1;
    return acc;
  }, {});

  const { score, label: healthLabel, color: healthColor, barColor,
          issues, warnings, recommendation } = computePlatformHealth(overview, bots, storageStats);

  const filteredBots = bots.filter(b =>
    !botSearch ||
    (b.name || b.botId || '').toLowerCase().includes(botSearch.toLowerCase()) ||
    (b.network || '').toLowerCase().includes(botSearch.toLowerCase())
  );

  const scalingChip = {
    healthy:  { text: 'Platform healthy — no action needed',      color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
    monitor:  { text: 'Growth detected — monitor resource usage', color: 'text-amber-400',   bg: 'bg-amber-500/10 border-amber-500/20'   },
    critical: { text: 'Attention required — review issues below', color: 'text-red-400',     bg: 'bg-red-500/10 border-red-500/20'       },
  }[recommendation];

  return (
    <div className="space-y-6">

      {/* Platform-wide scope notice */}
      <div className="flex items-center gap-3 px-4 py-3 bg-accent-primary/5 border border-accent-primary/15 rounded-2xl">
        <Globe className="w-4 h-4 text-accent-primary flex-shrink-0" />
        <p className="text-gray-400 text-xs leading-relaxed">
          <span className="text-accent-primary font-semibold">Platform-Wide View — </span>
          Every metric below aggregates data across <strong className="text-white">all users</strong> on your platform.
          As users sign up and create bots, these numbers grow automatically.
        </p>
        {lastRefresh && (
          <span className="text-gray-600 text-xs ml-auto whitespace-nowrap flex items-center gap-1 flex-shrink-0">
            <Clock className="w-3 h-3" /> {lastRefresh.toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* Live Trading Warning */}
      {!loading && isLive && (
        <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-2xl">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-400 font-semibold text-sm">Live Trading Mode Active — All bots are submitting real on-chain transactions</p>
            <p className="text-red-400/70 text-xs mt-0.5">Paper trading is disabled platform-wide. All user bots are using real funds.</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Spinner className="text-accent-primary" /></div>
      ) : (
        <>
          {/* ── 6 Hero stats ── */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {/* Users — future multi-user slot */}
            <div className="bg-dark-800 rounded-2xl p-5 border border-dashed border-dark-600 flex items-start gap-4 relative overflow-hidden">
              <div className="w-11 h-11 rounded-xl bg-purple-500/10 text-purple-400 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-gray-500 text-xs">Total Users</p>
                <p className="text-2xl font-bold mt-0.5 text-purple-400">{overview?.totalUsers ?? '—'}</p>
                <p className="text-gray-600 text-xs mt-0.5">Registered accounts</p>
              </div>
            </div>

            <StatHero label="Total Bots (Platform)" value={totalBots}
              icon={Server} color="text-accent-primary" bg="bg-accent-primary/10" pulse
              sub={`${activeBots} active · ${pausedBots} paused`} />

            <StatHero label="Today's Trades (Platform)" value={overview?.totalTrades ?? 0}
              icon={TrendingUp} color="text-emerald-400" bg="bg-emerald-500/10"
              sub="Across all bots & users" />

            <StatHero label="Storage Records" value={overview?.storageStats?.totalFiles ?? 0}
              icon={Database} color="text-blue-400" bg="bg-blue-500/10"
              sub={storageStats ? formatBytes(storageStats.totalSizeKb) : '—'} />

            <StatHero label="Platform Uptime" value={formatUptime(overview?.uptime)}
              icon={Clock} color="text-teal-400" bg="bg-teal-500/10"
              sub="Since last restart" />

            <StatHero label="Trading Mode"
              value={isLive ? 'Live' : 'Paper'}
              icon={isLive ? Zap : Shield}
              color={isLive ? 'text-red-400' : 'text-amber-400'}
              bg={isLive ? 'bg-red-500/10' : 'bg-amber-500/10'}
              sub={isLive ? 'Real funds at risk' : 'No real funds used'} />
          </div>

          {/* ── Platform Health Score ── */}
          <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-accent-primary/10 text-accent-primary flex items-center justify-center">
                  <Activity className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">Platform Health Score</h3>
                  <p className="text-gray-500 text-xs mt-0.5">Computed from uptime, bot status, storage, and API health</p>
                </div>
              </div>
              <button onClick={load} className="btn-ghost p-1.5">
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="flex items-end gap-4 mb-4">
              <p className={`text-5xl font-black ${healthColor}`}>{score}</p>
              <div className="pb-1">
                <p className={`text-lg font-bold ${healthColor}`}>{healthLabel}</p>
                <p className="text-gray-500 text-xs">out of 100</p>
              </div>
            </div>

            <div className="h-2.5 bg-dark-700 rounded-full overflow-hidden mb-4">
              <div className={`h-full ${barColor} rounded-full transition-all duration-700`} style={{ width: `${score}%` }} />
            </div>

            {/* Scaling recommendation chip */}
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-medium mb-4 ${scalingChip.bg}`}>
              <Wifi className={`w-3.5 h-3.5 ${scalingChip.color}`} />
              <span className={scalingChip.color}>{scalingChip.text}</span>
            </div>

            {/* Issues & warnings */}
            {(issues.length > 0 || warnings.length > 0) && (
              <div className="space-y-2">
                {issues.map((iss, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-red-400 bg-red-500/5 px-3 py-2 rounded-xl">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />{iss}
                  </div>
                ))}
                {warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-amber-400 bg-amber-500/5 px-3 py-2 rounded-xl">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />{w}
                  </div>
                ))}
              </div>
            )}

            {/* Storage source indicator — real, not a row of fake green pills */}
            {overview?.storageStats?.source && (
              <div className="flex items-center gap-2 mt-4 px-3 py-2 bg-dark-700/30 rounded-xl border border-dark-600/30">
                <Database className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-gray-500 text-xs">Storage backend</span>
                <span className={`text-xs font-medium ml-auto ${
                  overview.storageStats.source === 'adls' ? 'text-emerald-400'
                    : overview.storageStats.source === 'local-fs' ? 'text-amber-400'
                    : 'text-red-400'
                }`}>
                  {overview.storageStats.source === 'adls' ? 'Cloud Storage'
                    : overview.storageStats.source === 'local-fs' ? 'Local (dev)'
                    : 'Unavailable'}
                </span>
              </div>
            )}
          </div>

          {/* ── Scaling Indicators ── */}
          <SectionCard icon={TrendingUp} title="Scaling Indicators"
            subtitle="Use these to decide when to expand infrastructure capacity">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Bot capacity */}
              {(() => {
                const botCapPct = Math.min(100, Math.round((totalBots / 100) * 100));
                const capColor  = botCapPct < 50 ? 'bg-emerald-500' : botCapPct < 80 ? 'bg-amber-500' : 'bg-red-500';
                const capText   = botCapPct < 50 ? 'text-emerald-400' : botCapPct < 80 ? 'text-amber-400' : 'text-red-400';
                return (
                  <div className="bg-dark-700/40 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-gray-400 text-xs font-medium">Bot Capacity</p>
                      <span className={`text-sm font-bold ${capText}`}>{totalBots} / 100</span>
                    </div>
                    <div className="h-1.5 bg-dark-600 rounded-full overflow-hidden">
                      <div className={`h-full ${capColor} rounded-full`} style={{ width: `${botCapPct}%` }} />
                    </div>
                    <p className="text-gray-600 text-xs">
                      {botCapPct < 50 ? 'Comfortable headroom' : botCapPct < 80 ? 'Monitor growth' : 'Near capacity — plan scaling'}
                    </p>
                  </div>
                );
              })()}

              {/* Storage capacity */}
              {(() => {
                const storKb    = storageStats?.totalSizeKb ?? 0;
                const storPct   = Math.min(100, Math.round((storKb / 102400) * 100)); // 100 MB threshold
                const storColor = storPct < 50 ? 'bg-emerald-500' : storPct < 80 ? 'bg-amber-500' : 'bg-red-500';
                const storText  = storPct < 50 ? 'text-emerald-400' : storPct < 80 ? 'text-amber-400' : 'text-red-400';
                return (
                  <div className="bg-dark-700/40 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-gray-400 text-xs font-medium">Storage Usage</p>
                      <span className={`text-sm font-bold ${storText}`}>{formatBytes(storKb)} / 100 MB</span>
                    </div>
                    <div className="h-1.5 bg-dark-600 rounded-full overflow-hidden">
                      <div className={`h-full ${storColor} rounded-full`} style={{ width: `${storPct}%` }} />
                    </div>
                    <p className="text-gray-600 text-xs">
                      {storPct < 50 ? 'Well within limits' : storPct < 80 ? 'Growing — set purge schedule' : 'High usage — purge immediately'}
                    </p>
                  </div>
                );
              })()}

              {/* Data records */}
              {(() => {
                const recPct   = Math.min(100, Math.round(((overview?.storageStats?.totalFiles ?? 0) / 10000) * 100));
                const recColor = recPct < 50 ? 'bg-emerald-500' : recPct < 80 ? 'bg-amber-500' : 'bg-red-500';
                const recText  = recPct < 50 ? 'text-emerald-400' : recPct < 80 ? 'text-amber-400' : 'text-red-400';
                return (
                  <div className="bg-dark-700/40 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-gray-400 text-xs font-medium">Record Volume</p>
                      <span className={`text-sm font-bold ${recText}`}>{overview?.storageStats?.totalFiles ?? 0} / 10k</span>
                    </div>
                    <div className="h-1.5 bg-dark-600 rounded-full overflow-hidden">
                      <div className={`h-full ${recColor} rounded-full`} style={{ width: `${recPct}%` }} />
                    </div>
                    <p className="text-gray-600 text-xs">
                      {recPct < 50 ? 'Normal volume' : recPct < 80 ? 'Increasing — consider archiving' : 'High volume — archive old records'}
                    </p>
                  </div>
                );
              })()}
            </div>
          </SectionCard>

          {/* ── Security Monitor ── */}
          <SectionCard icon={Shield} title="Security Monitor"
            subtitle="Platform-wide security posture — admin only">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Auth Method',      value: 'HMAC-SHA256',            ok: true  },
                { label: 'Token Expiry',     value: '24 hours',               ok: true  },
                { label: 'Active Sessions',  value: `${overview?.activeSessions ?? 0}`, ok: true },
                { label: 'Admin Access',     value: 'Current session',        ok: true  },
              ].map(({ label, value, ok }) => (
                <div key={label} className="bg-dark-700/40 rounded-xl p-4">
                  <div className="flex items-center gap-1.5 mb-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${ok ? 'bg-emerald-400' : 'bg-red-400'}`} />
                    <p className="text-gray-500 text-xs">{label}</p>
                  </div>
                  <p className="text-white text-sm font-semibold">{value}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-start gap-2 p-3 bg-dark-700/30 rounded-xl border border-dark-600/30">
              <Info className="w-3.5 h-3.5 text-gray-500 flex-shrink-0 mt-0.5" />
              <p className="text-gray-600 text-xs leading-relaxed">
                This page is only accessible to authenticated admins. Unauthenticated requests are rejected before reaching any data.
                For advanced security auditing, review the Event Stream tab and filter by severity level "error".
              </p>
            </div>
          </SectionCard>

          {/* ── Network Distribution ── */}
          {Object.keys(networkDist).length > 0 && (
            <SectionCard icon={Globe} title="Network Distribution"
              subtitle="Which blockchains your platform bots are running on">
              <div className="space-y-3">
                {Object.entries(networkDist).map(([net, count]) => {
                  const pct = Math.round((count / totalBots) * 100);
                  return (
                    <div key={net}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-gray-300 text-sm">{net}</span>
                        <span className="text-white text-xs font-medium">{count} bot{count !== 1 ? 's' : ''} <span className="text-gray-600">({pct}%)</span></span>
                      </div>
                      <div className="h-1.5 bg-dark-700 rounded-full overflow-hidden">
                        <div className="h-full bg-accent-primary rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          )}

          {/* ── Platform Bot Registry ── */}
          <SectionCard icon={Server} title="Platform Bot Registry"
            subtitle="All bots registered across all users on this platform"
            action={
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500" />
                  <input type="text" value={botSearch} onChange={e => setBotSearch(e.target.value)}
                    placeholder="Search bots…"
                    className="bg-dark-700 border border-dark-600 rounded-lg pl-7 pr-3 py-1.5 text-white text-xs focus:outline-none focus:border-accent-primary w-36 placeholder-gray-600" />
                </div>
                <button onClick={load} className="btn-ghost p-1.5"><RefreshCw className="w-3.5 h-3.5" /></button>
              </div>
            }>

            {/* Legend */}
            <div className="flex items-center gap-4 mb-3 text-xs text-gray-600">
              <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Active</div>
              <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 rounded-full bg-amber-400" /> Paused</div>
              <span className="ml-auto text-gray-600">{filteredBots.length} bot{filteredBots.length !== 1 ? 's' : ''} shown</span>
            </div>

            {filteredBots.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">No bots match your search.</div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {filteredBots.map(bot => (
                  <div key={bot.botId} className="flex items-center gap-3 bg-dark-700/40 rounded-xl px-4 py-3">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${bot.status === 'paused' ? 'bg-amber-400' : 'bg-emerald-400'}`}
                      style={bot.status !== 'paused' ? { boxShadow: '0 0 4px #34d399' } : undefined} />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{bot.name || bot.botId}</p>
                      <p className="text-gray-500 text-xs">{NETWORK_LABELS[bot.network] || bot.network || 'Unknown'} · {bot.pair || 'MCN/USDC'}</p>
                    </div>
                    {/* Owner column — real userId from bot config (multi-user live) */}
                    <span className="text-gray-600 text-xs px-2 py-0.5 bg-dark-700 rounded hidden md:inline font-mono"
                      title={bot.userId || 'Unknown owner'}>
                      owner: {bot.userId ? (bot.userId.length > 10 ? `${bot.userId.slice(0, 8)}…` : bot.userId) : '—'}
                    </span>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ${bot.status === 'paused' ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                      {bot.status || 'active'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 flex items-start gap-2 p-3 bg-dark-700/30 rounded-xl border border-dark-600/20">
              <Info className="w-3.5 h-3.5 text-gray-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-600 text-xs leading-relaxed">
                Owner shows each bot's user ID (truncated). Hover for the full ID.
                Bots without a userId field show <span className="font-mono">—</span>.
              </p>
            </div>
          </SectionCard>

          {/* ── Operations ── */}
          <SectionCard icon={Zap} title="Operations" subtitle="Manually trigger platform-wide functions">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-dark-700/50 rounded-xl p-4 border border-dark-600/30">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/15 text-emerald-400 flex items-center justify-center flex-shrink-0">
                    <Activity className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">Health Check</p>
                    <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">Full diagnostic sweep and refresh the platform health score above.</p>
                    <button onClick={trigger} disabled={!!triggering} className="btn-secondary text-xs flex items-center gap-1.5 mt-3">
                      {triggering ? <><Spinner size="sm" /> Checking...</> : <><RefreshCw className="w-3 h-3" /> Run Check</>}
                    </button>
                  </div>
                </div>
              </div>
              <div className="bg-dark-700/50 rounded-xl p-4 border border-dark-600/30 flex items-center justify-center text-center">
                <p className="text-gray-500 text-xs leading-relaxed">
                  Additional manual ops (forecast resolver, CCXT refresh, verification re-send)
                  live under <span className="text-accent-primary">Settings → Operations</span>.
                </p>
              </div>
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}

// ─── Tab: Platform Config ─────────────────────────────────────────────────────

function ConfigKpiCard({ label, value, sub, tone = 'neutral', icon: Icon }) {
  const toneClass = {
    good:    'text-emerald-400 bg-emerald-500/10',
    warn:    'text-amber-400 bg-amber-500/10',
    danger:  'text-red-400 bg-red-500/10',
    neutral: 'text-gray-300 bg-dark-700/60',
  }[tone];
  return (
    <div className="bg-dark-800 rounded-2xl p-4 border border-dark-700/50">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-500 text-xs uppercase tracking-wide">{label}</span>
        {Icon && <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${toneClass}`}><Icon className="w-3.5 h-3.5" /></div>}
      </div>
      <p className={`text-lg font-bold ${tone === 'good' ? 'text-emerald-400' : tone === 'warn' ? 'text-amber-400' : tone === 'danger' ? 'text-red-400' : 'text-white'}`}>{value}</p>
      {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
    </div>
  );
}

function StatusPill({ tone, children }) {
  const cls = {
    good:    'bg-emerald-500/10 text-emerald-400',
    warn:    'bg-amber-500/10 text-amber-400',
    danger:  'bg-red-500/10 text-red-400',
    neutral: 'bg-dark-600 text-gray-400',
  }[tone] || 'bg-dark-600 text-gray-400';
  return <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${cls}`}>{children}</span>;
}

function ReadOnlyRow({ label, description, status, statusTone }) {
  return (
    <div className="flex items-start justify-between gap-4 p-3 rounded-xl bg-dark-700/40">
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium">{label}</p>
        {description && <p className="text-gray-500 text-xs mt-0.5">{description}</p>}
      </div>
      <StatusPill tone={statusTone}>{status}</StatusPill>
    </div>
  );
}

// Editable feature flags are persisted via featureFlags.setFlag() → ADLS.
// Env-only flags can't be flipped at runtime (set in Azure App Settings).
const EDITABLE_FLAGS = new Set(['PAID_CHANNELS_ENABLED', 'PAID_CHANNELS_INVITE_ONLY']);

function FeatureFlagRow({ flag, onToggle, busy }) {
  const [confirming, setConfirming] = useState(false);
  const editable = EDITABLE_FLAGS.has(flag.key);
  const on = !!flag.enabled;

  if (!editable) {
    return (
      <div className="flex items-start justify-between gap-4 p-3 rounded-xl bg-dark-700/40">
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium">{flag.label}</p>
          <p className="text-gray-500 text-xs mt-0.5"><code className="text-gray-600">{flag.key}</code> — set via deployment configuration</p>
        </div>
        <StatusPill tone={on ? 'good' : 'neutral'}>{on ? 'Enabled' : 'Disabled'}</StatusPill>
      </div>
    );
  }

  // Two-step confirm for editable flags. First click stages the change,
  // second click commits — no native confirm() dialog, no surprise toggles.
  return (
    <div className={`flex items-start justify-between gap-4 p-3 rounded-xl border transition-colors ${
      confirming ? 'bg-amber-500/5 border-amber-500/30'
                 : on ? 'bg-emerald-500/5 border-emerald-500/15'
                      : 'bg-dark-700/40 border-dark-600/30'}`}>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium">{flag.label}</p>
        <p className="text-gray-500 text-xs mt-0.5">
          {confirming
            ? <>Will switch to <span className={on ? 'text-red-400' : 'text-emerald-400'}>{on ? 'Disabled' : 'Enabled'}</span> for all users — click again to confirm</>
            : <><code className="text-gray-600">{flag.key}</code></>}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={`text-xs font-bold ${on ? 'text-emerald-400' : 'text-red-400'}`}>{on ? 'ON' : 'OFF'}</span>
        {confirming ? (
          <>
            <button onClick={() => { setConfirming(false); onToggle(flag.key, !on); }} disabled={busy}
              className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1">
              {busy ? <Spinner size="sm" /> : <Check className="w-3 h-3" />} Confirm
            </button>
            <button onClick={() => setConfirming(false)} disabled={busy}
              className="btn-ghost text-xs px-3 py-1.5">Cancel</button>
          </>
        ) : (
          <button onClick={() => setConfirming(true)} disabled={busy}
            className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1">
            {on ? 'Disable' : 'Enable'}
          </button>
        )}
      </div>
    </div>
  );
}

function PlatformConfigTab({ onToast }) {
  const [config,  setConfig]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [showInfra,   setShowInfra]   = useState(false);
  const [togglingFlag, setTogglingFlag] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminConfig();
      setConfig(data);
    } catch (err) { onToast({ type: 'error', text: err.message }); }
    finally { setLoading(false); }
  }, [onToast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  const toggleFlag = useCallback(async (key, value) => {
    setTogglingFlag(key);
    try {
      await togglePaidChannelFlag(key, value);
      onToast({ type: 'success', text: `${key} → ${value ? 'Enabled' : 'Disabled'}` });
      await load();
    } catch (err) {
      onToast({ type: 'error', text: err.message || 'Toggle failed' });
    } finally {
      setTogglingFlag(null);
    }
  }, [load, onToast]);

  if (loading || !config) return <div className="flex justify-center py-16"><Spinner className="text-accent-primary" /></div>;

  const rpcs = config.rpcs || [];
  const rpcsReachable = rpcs.filter(r => r.reachable).length;

  const flags = config.featureFlags || [];
  const flagsOn = flags.filter(f => f.enabled).length;
  const editableCount = flags.filter(f => EDITABLE_FLAGS.has(f.key)).length;

  const refreshedAt = config.generatedAt ? new Date(config.generatedAt) : null;

  return (
    <div className="space-y-6">
      {/* Header — refreshed at + auto-refresh */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-gray-500 text-xs flex items-center gap-1.5">
          <Clock className="w-3 h-3" />
          {refreshedAt ? `Refreshed ${refreshedAt.toLocaleTimeString()}` : '—'}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setAutoRefresh(v => !v)}
            className={`text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-xl border transition-colors ${
              autoRefresh ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-primary' : 'btn-ghost border-dark-600'}`}>
            <Radio className="w-3 h-3" /> {autoRefresh ? 'Auto-refresh on' : 'Auto-refresh off'}
          </button>
          <button onClick={load} disabled={loading} className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* KPI strip — tight, only the signals you can't see elsewhere */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ConfigKpiCard label="Feature flags" value={`${flagsOn}/${flags.length} on`} icon={ToggleLeft}
          tone={flagsOn > 0 ? 'good' : 'neutral'}
          sub={`${editableCount} editable here`} />
        <ConfigKpiCard label="RPC providers" value={`${rpcsReachable}/${rpcs.length}`} icon={Wifi}
          tone={rpcsReachable === rpcs.length ? 'good' : rpcsReachable > 0 ? 'warn' : 'danger'}
          sub={rpcsReachable === rpcs.length ? 'All chains reachable' : `${rpcs.length - rpcsReachable} unreachable`} />
        <ConfigKpiCard label="Runtime"
          value={config.infrastructure?.nodeEnv || 'production'} icon={Server}
          tone="neutral"
          sub={`${config.infrastructure?.storageMode || 'cloud'} storage · ${config.infrastructure?.secretStore || 'managed'} secrets`} />
      </div>

      {/* Feature Flags — INTERACTIVE for editable, read-only for env-only */}
      <SectionCard icon={ToggleLeft} title="Feature Flags"
        subtitle="Global toggles affecting every user. Editable flags persist to storage and take effect within 60s">
        <div className="space-y-2">
          {flags.map(f => (
            <FeatureFlagRow key={f.key} flag={f} onToggle={toggleFlag} busy={togglingFlag === f.key} />
          ))}
        </div>
      </SectionCard>

      {/* RPC providers — actual eth_blockNumber probe + latency + block height */}
      <SectionCard icon={Wifi} title="RPC Providers"
        subtitle="Live eth_blockNumber probe per chain — URLs are never returned">
        <div className="space-y-2">
          {rpcs.map(rpc => (
            <div key={rpc.envVar} className="flex items-center gap-3 bg-dark-700/40 rounded-xl px-4 py-3">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${rpc.reachable ? 'bg-emerald-400' : 'bg-red-400'}`}
                style={rpc.reachable ? { boxShadow: '0 0 5px #34d399' } : undefined} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-gray-200 text-sm">{rpc.label}</p>
                  {rpc.usingFallback && <span className="text-amber-400/80 text-[10px] uppercase tracking-wide bg-amber-500/10 px-1.5 py-0.5 rounded">Fallback</span>}
                </div>
                <p className="text-gray-500 text-xs truncate" title={rpc.error || ''}>
                  {rpc.reachable
                    ? <>via {rpc.provider || 'unknown'} · {rpc.latencyMs}ms · block {rpc.blockHeight?.toLocaleString()}</>
                    : <>via {rpc.provider || 'unknown'} · {rpc.error || 'unreachable'}</>}
                </p>
              </div>
              <StatusPill tone={rpc.reachable ? 'good' : 'danger'}>
                {rpc.reachable ? 'Reachable' : 'Unreachable'}
              </StatusPill>
            </div>
          ))}
          {rpcs.length === 0 && <p className="text-gray-500 text-sm text-center py-4">No RPC configuration found</p>}
        </div>
      </SectionCard>

      {/* Infrastructure — collapsible */}
      <SectionCard icon={Server} title="Infrastructure" subtitle="Read-only platform context — change via deployment configuration"
        action={
          <button onClick={() => setShowInfra(v => !v)} className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5">
            {showInfra ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {showInfra ? 'Hide' : 'Show'}
          </button>
        }>
        {showInfra && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              { label: 'Runtime environment', value: config.infrastructure?.nodeEnv || 'production' },
              { label: 'Storage mode',        value: config.infrastructure?.storageMode === 'cloud' ? 'Cloud' : 'Local' },
              { label: 'Secret store',        value: config.infrastructure?.secretStore === 'managed' ? 'Managed' : 'Local' },
              { label: 'Storage container',   value: config.infrastructure?.container || '—' },
              { label: 'Trade queue',         value: config.infrastructure?.queue || 'Disabled' },
            ].map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between p-3 bg-dark-700/40 rounded-xl">
                <span className="text-gray-500 text-xs">{label}</span>
                <span className="text-xs font-medium text-white">{value}</span>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// ─── Tab: Event Stream ────────────────────────────────────────────────────────

const LEVEL_STYLE = {
  critical: { row: 'bg-red-500/10 border-l-2 border-red-500',        badge: 'bg-red-500/20 text-red-300'    },
  error:    { row: 'bg-red-500/10 border-l-2 border-red-500',        badge: 'bg-red-500/20 text-red-300'    },
  warning:  { row: 'bg-amber-500/8 border-l-2 border-amber-500',     badge: 'bg-amber-500/20 text-amber-300' },
  warn:     { row: 'bg-amber-500/8 border-l-2 border-amber-500',     badge: 'bg-amber-500/20 text-amber-300' },
  info:     { row: 'bg-blue-500/5 border-l-2 border-blue-500/40',    badge: 'bg-blue-500/20 text-blue-300'  },
  debug:    { row: 'bg-dark-800 border-l-2 border-dark-600',         badge: 'bg-dark-600 text-gray-400'     },
};

function EventStreamTab({ botIds }) {
  const [logs,       setLogs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [botFilter,  setBotFilter]  = useState('');
  const [sevFilter,  setSevFilter]  = useState('');
  const [search,     setSearch]     = useState('');
  const [autoRefresh,setAutoRefresh]= useState(false);
  const intervalRef = useRef(null);

  const load = useCallback(async (bot = botFilter) => {
    setLoading(true);
    try { setLogs((await getAdminEventLogs(bot, 200)) || []); }
    catch {}
    finally { setLoading(false); }
  }, [botFilter]);

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (autoRefresh) { intervalRef.current = setInterval(() => load(), 30000); }
    else             { clearInterval(intervalRef.current); }
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, load]);

  const filtered = logs.filter(log => {
    const level = (log.severity || log.level || 'info').toLowerCase();
    if (sevFilter && level !== sevFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (log.message || '').toLowerCase().includes(q)
          || (log.event || '').toLowerCase().includes(q)
          || (log.botId || '').toLowerCase().includes(q);
    }
    return true;
  });

  const counts = logs.reduce((acc, l) => {
    const lv = (l.severity || l.level || 'info').toLowerCase();
    if (lv === 'error' || lv === 'critical') acc.errors++;
    else if (lv === 'warning' || lv === 'warn') acc.warnings++;
    else acc.info++;
    return acc;
  }, { errors: 0, warnings: 0, info: 0 });

  return (
    <div className="space-y-4">
      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Errors',   value: counts.errors,   color: 'text-red-400',    active: sevFilter === 'error',   key: 'error'   },
          { label: 'Warnings', value: counts.warnings, color: 'text-amber-400',  active: sevFilter === 'warning', key: 'warning' },
          { label: 'Info',     value: counts.info,     color: 'text-blue-400',   active: sevFilter === 'info',    key: 'info'    },
        ].map(({ label, value, color, active, key }) => (
          <button key={key} onClick={() => setSevFilter(active ? '' : key)}
            className={`rounded-xl p-4 text-left border transition-colors ${active ? 'bg-dark-700 border-accent-primary/40' : 'bg-dark-800 border-dark-700/50 hover:border-dark-600'}`}>
            <p className={`text-xl font-bold ${color}`}>{value}</p>
            <p className="text-gray-500 text-xs mt-0.5">{label}</p>
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search events, messages, bots…"
            className="w-full bg-dark-700 border border-dark-600 rounded-xl pl-9 pr-3 py-2 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
        </div>
        <select value={botFilter} onChange={e => { setBotFilter(e.target.value); load(e.target.value); }}
          className="bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-accent-primary">
          <option value="">All Bots</option>
          {(botIds || []).map(id => <option key={id} value={id}>{id}</option>)}
        </select>
        <button onClick={() => load()} className="btn-ghost text-xs flex items-center gap-1.5 px-3 py-2">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
        <button onClick={() => setAutoRefresh(v => !v)}
          className={`text-xs flex items-center gap-1.5 px-3 py-2 rounded-xl border transition-colors ${autoRefresh ? 'bg-accent-primary/10 border-accent-primary/40 text-accent-primary' : 'btn-ghost border-dark-600'}`}>
          <Radio className="w-3.5 h-3.5" /> {autoRefresh ? 'Live' : 'Paused'}
        </button>
        <span className="text-gray-600 text-xs ml-auto">{filtered.length} / {logs.length} events</span>
      </div>

      {/* Log rows */}
      {loading && logs.length === 0 ? (
        <div className="flex justify-center py-12"><Spinner className="text-accent-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-dark-800 rounded-2xl p-10 border border-dark-700/50 text-center">
          <ScrollText className="w-8 h-8 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">No events match your filters.</p>
        </div>
      ) : (
        <div className="space-y-1 max-h-[560px] overflow-y-auto pr-1 rounded-xl">
          {filtered.map((log, idx) => {
            const level = (log.severity || log.level || 'info').toLowerCase();
            const style = LEVEL_STYLE[level] || LEVEL_STYLE.info;
            return (
              <div key={idx} className={`px-4 py-3 rounded-r-xl text-xs ${style.row}`}>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="text-gray-500">{log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}</span>
                  {log.botId && <span className="text-accent-primary font-mono">{log.botId}</span>}
                  {log.event && <span className="text-gray-300 font-semibold">{log.event}</span>}
                  <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${style.badge}`}>{level}</span>
                </div>
                <p className="text-gray-300 leading-relaxed">{log.message || JSON.stringify(log)}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Tab: Storage ─────────────────────────────────────────────────────────────

function StorageTab({ onToast }) {
  const { runWithStepUp } = useStepUpGate();
  const [stats,    setStats]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [purging,  setPurging]  = useState(false);
  const [purgeDays,setPurgeDays]= useState(90);

  const load = useCallback(async () => {
    setLoading(true);
    try { setStats(await getAdminStorageStats()); }
    catch (err) { onToast({ type: 'error', text: err.message }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePurge = async () => {
    if (!window.confirm(`Permanently delete all trade history older than ${purgeDays} days?`)) return;
    setPurging(true);
    try {
      const res = await runWithStepUp(
        () => purgeOldStorage(purgeDays),
        `Purge data older than ${purgeDays} days`
      );
      onToast({ type: 'success', text: `Purged ${res?.deletedFiles ?? 0} files older than ${purgeDays} days` });
      await load();
    } catch (err) {
      if (err.code === 'APPROVAL_REQUIRED') {
        onToast({ type: 'success', text: `Approval requested — see Approvals tab to track #${err.approvalId?.slice(0, 8)}` });
      } else { onToast({ type: 'error', text: err.message }); }
    }
    finally { setPurging(false); }
  };

  const breakdown = stats?.breakdown || {};
  const total = stats?.totalFiles || 0;
  const pct = (n) => total > 0 ? Math.round((n / total) * 100) : 0;

  const categories = [
    { key: 'trades',    label: 'Trade History',   color: 'bg-accent-primary' },
    { key: 'configs',   label: 'Bot Configs',      color: 'bg-blue-500'       },
    { key: 'counters',  label: 'Daily Counters',   color: 'bg-emerald-500'    },
    { key: 'summaries', label: 'Summaries',         color: 'bg-purple-500'     },
    { key: 'events',    label: 'Event Logs',        color: 'bg-amber-500'      },
    { key: 'signals',   label: 'Signal Data',       color: 'bg-red-500'        },
  ];

  return (
    <div className="space-y-6">
      {/* Summary stats */}
      {loading ? (
        <div className="flex justify-center py-12"><Spinner className="text-accent-primary" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4">
            <StatHero label="Total Records"     value={stats?.totalFiles ?? 0}               icon={Database} />
            <StatHero label="Storage Used"      value={formatBytes(stats?.totalSizeKb ?? 0)} icon={HardDrive} color="text-blue-400" bg="bg-blue-500/10" />
          </div>

          {/* Breakdown */}
          <SectionCard icon={BarChart3} title="Data Breakdown" subtitle="Records by category"
            action={<button onClick={load} className="btn-ghost p-1.5"><RefreshCw className="w-3.5 h-3.5" /></button>}>
            <div className="space-y-3">
              {categories.map(({ key, label, color }) => (
                <div key={key}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-gray-400 text-xs">{label}</span>
                    <span className="text-white text-xs font-medium">{breakdown[key] ?? 0} <span className="text-gray-600">({pct(breakdown[key] ?? 0)}%)</span></span>
                  </div>
                  <div className="h-1.5 bg-dark-700 rounded-full overflow-hidden">
                    <div className={`h-full ${color} rounded-full transition-all duration-500`}
                      style={{ width: `${pct(breakdown[key] ?? 0)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Purge */}
          <SectionCard icon={Trash2} title="Data Retention" subtitle="Remove old records to free storage space" danger>
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-red-500/5 rounded-xl border border-red-500/15">
                <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-400 text-xs leading-relaxed">
                  Purge permanently deletes trade history and daily counter files older than the selected period.
                  Bot configurations, signals, and summaries are never purged.
                </p>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-gray-400 text-sm">Delete records older than</span>
                <div className="flex items-center gap-2">
                  <input type="number" value={purgeDays} onChange={e => setPurgeDays(parseInt(e.target.value) || 30)}
                    min={7} max={365}
                    className="w-20 bg-dark-700 border border-dark-600 rounded-xl px-3 py-2 text-white text-sm text-right focus:outline-none focus:border-red-400" />
                  <span className="text-gray-400 text-sm">days</span>
                </div>
                <button onClick={handlePurge} disabled={purging}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl text-sm hover:bg-red-500/20 transition-colors ml-auto">
                  {purging ? <Spinner size="sm" /> : <Trash2 className="w-3.5 h-3.5" />}
                  Purge Old Data
                </button>
              </div>
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}

// ─── Tab: Ad Management ───────────────────────────────────────────────────────

const ACCENT_COLORS = ['amber', 'violet', 'emerald', 'blue', 'rose'];
const COLOR_MAP = {
  amber:   { bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   text: 'text-amber-400'   },
  violet:  { bg: 'bg-violet-500/10',  border: 'border-violet-500/30',  text: 'text-violet-400'  },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400' },
  blue:    { bg: 'bg-blue-500/10',    border: 'border-blue-500/30',    text: 'text-blue-400'    },
  rose:    { bg: 'bg-rose-500/10',    border: 'border-rose-500/30',    text: 'text-rose-400'    },
};

const DEFAULT_AD_CONFIG = {
  header: { enabled: false, imageUrl: '', linkUrl: '', headline: 'Advertise Here', subText: 'Reach thousands of active DeFi traders', cta: 'Learn More' },
  slots: [
    { id: 'slot1', enabled: false, tag: 'Sponsored', imageUrl: '', linkUrl: '', headline: 'Your Ad Here', body: '300×250 · Premium placement', cta: 'Learn More', accentColor: 'amber' },
    { id: 'slot2', enabled: false, tag: 'Sponsored', imageUrl: '', linkUrl: '', headline: 'Your Ad Here', body: '300×250 · Prime sidebar spot', cta: 'Get Started', accentColor: 'violet' },
    { id: 'slot3', enabled: false, tag: 'Sponsored', imageUrl: '', linkUrl: '', headline: 'Your Ad Here', body: '300×250 · High-visibility slot', cta: 'Contact Us', accentColor: 'emerald' },
    { id: 'slot4', enabled: false, tag: 'Sponsored', imageUrl: '', linkUrl: '', headline: 'Your Ad Here', body: '300×250 · Premium below-fold spot', cta: 'Learn More', accentColor: 'cyan' },
    { id: 'slot5', enabled: false, tag: 'Sponsored', imageUrl: '', linkUrl: '', headline: 'Your Ad Here', body: '300×250 · Last inventory slot', cta: 'Get Started', accentColor: 'rose' },
  ]
};

// Merge saved server config with DEFAULT_AD_CONFIG so newly-added slots (e.g.
// slot4 + slot5 added 2026-06-05) appear in the Admin UI even if the saved
// blob was written when only 3 slots existed. Once the admin saves, the new
// slots get persisted server-side and this fallback is no longer needed —
// but it keeps the bridge across the upgrade.
function mergeAdConfig(saved) {
  if (!saved) return DEFAULT_AD_CONFIG;
  const savedSlotsById = new Map((saved.slots || []).map(s => [s.id, s]));
  const mergedSlots = DEFAULT_AD_CONFIG.slots.map(def => savedSlotsById.get(def.id) || def);
  return {
    header: saved.header || DEFAULT_AD_CONFIG.header,
    slots: mergedSlots,
  };
}

function AdField({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-accent-primary/50"
      />
    </div>
  );
}

function normalizeAdUrl(raw) {
  if (!raw || !raw.trim()) return raw;
  let url = raw.trim();
  url = url.replace(/^(https?)[;,]\/?\/?/i, '$1://');
  if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
  return url;
}

function isValidAdUrl(url) {
  if (!url) return false;
  try { new URL(url); return true; } catch { return false; }
}

function UrlField({ label, value, onChange, placeholder = 'https://example.com' }) {
  const [error, setError] = useState('');
  function handleBlur() {
    if (!value) return;
    const normalized = normalizeAdUrl(value);
    if (normalized !== value) onChange(normalized);
    setError(isValidAdUrl(normalized) ? '' : 'Enter a valid URL (e.g. https://yoursite.com)');
  }
  const valid = value && isValidAdUrl(value);
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={e => { onChange(e.target.value); setError(''); }}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`w-full bg-dark-700 border rounded-lg px-3 py-2 pr-7 text-sm text-white placeholder-gray-600 focus:outline-none transition-colors ${
            error ? 'border-red-500/60' : valid ? 'border-emerald-500/50' : 'border-dark-600 focus:border-accent-primary/50'
          }`}
        />
        {valid && !error && (
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-emerald-400 text-xs">✓</span>
        )}
      </div>
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}

function ImageUploadField({ label, value, onChange, specs }) {
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const [dragOver, setDragOver] = useState(false);

  async function handleFile(file) {
    if (!file) return;
    setUploadErr('');
    setUploading(true);
    try {
      const url = await uploadAdImage(file);
      onChange(url);
    } catch (e) {
      setUploadErr(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      <label className="block text-xs text-gray-400 mb-2">{label}</label>

      {value ? (
        /* ── Preview state ── */
        <div className="relative rounded-xl overflow-hidden border border-dark-600 bg-dark-700">
          <img src={value} alt="Banner preview"
            className="w-full max-h-40 object-contain bg-dark-800"
            onError={e => e.target.style.display='none'} />
          <div className="absolute top-2 right-2 flex gap-1.5">
            <label className="flex items-center gap-1 px-2.5 py-1.5 bg-dark-900/80 backdrop-blur rounded-lg text-xs text-gray-300 cursor-pointer hover:text-white transition-colors border border-dark-600/60">
              <input type="file" accept=".png,.jpg,.jpeg,.gif,.webp" className="hidden"
                disabled={uploading} onChange={e => handleFile(e.target.files[0])} />
              <RefreshCw className="w-3 h-3" /> Replace
            </label>
            <button onClick={() => onChange('')}
              className="flex items-center gap-1 px-2.5 py-1.5 bg-dark-900/80 backdrop-blur rounded-lg text-xs text-red-400 hover:text-red-300 transition-colors border border-dark-600/60">
              <X className="w-3 h-3" /> Remove
            </button>
          </div>
        </div>
      ) : (
        /* ── Drop zone state ── */
        <label
          className={`relative flex flex-col items-center justify-center gap-2 w-full py-8 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
            dragOver
              ? 'border-accent-primary/60 bg-accent-primary/5'
              : 'border-dark-600 bg-dark-700/40 hover:border-dark-500 hover:bg-dark-700/60'
          } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
        >
          <input type="file" accept=".png,.jpg,.jpeg,.gif,.webp" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={uploading} onChange={e => handleFile(e.target.files[0])} />
          {uploading ? (
            <RefreshCw className="w-6 h-6 text-accent-primary animate-spin" />
          ) : (
            <Image className="w-6 h-6 text-gray-500" />
          )}
          <p className="text-sm text-gray-300 font-medium">
            {uploading ? 'Uploading…' : 'Click to upload or drag & drop'}
          </p>
          <p className="text-xs text-gray-600">{specs || 'PNG, JPG, GIF, WebP · max 3MB'}</p>
        </label>
      )}

      {/* Optional: paste a URL directly */}
      {!value && (
        <div className="mt-2">
          <input type="text" value="" onChange={e => e.target.value && onChange(e.target.value)}
            placeholder="…or paste an image URL"
            className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-1.5 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-accent-primary/50" />
        </div>
      )}

      {uploadErr && <p className="text-xs text-red-400 mt-1.5">{uploadErr}</p>}
    </div>
  );
}

// Countdown display for active ads
function Countdown({ expiresAt }) {
  const [remaining, setRemaining] = useState('');

  useEffect(() => {
    function update() {
      const diff = new Date(expiresAt) - new Date();
      if (diff <= 0) { setRemaining('Expired'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m ${s}s`);
    }
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);

  const isUrgent = new Date(expiresAt) - new Date() < 3600000;
  return (
    <span className={`text-xs font-mono font-bold ${isUrgent ? 'text-red-400' : 'text-emerald-400'}`}>
      ⏱ {remaining}
    </span>
  );
}

const STATUS_STYLES = {
  pending_payment: { bg: 'bg-gray-500/10',    text: 'text-gray-400',    label: 'Pending Payment' },
  paid:            { bg: 'bg-amber-500/15',   text: 'text-amber-400',   label: 'Awaiting Review' },
  approved:        { bg: 'bg-emerald-500/15', text: 'text-emerald-400', label: 'Approved'         },
  rejected:        { bg: 'bg-red-500/15',     text: 'text-red-400',     label: 'Rejected'         },
};

const SLOT_OPTIONS = [
  { id: 'header', label: 'Header Banner (728×90)' },
  { id: 'slot1',  label: 'Sidebar Slot 1 (300×250)' },
  { id: 'slot2',  label: 'Sidebar Slot 2 (300×250)' },
  { id: 'slot3',  label: 'Sidebar Slot 3 (300×250)' },
  { id: 'slot4',  label: 'Sidebar Slot 4 (300×250)' },
  { id: 'slot5',  label: 'Sidebar Slot 5 (300×250)' },
];

function AdRequestCard({ req, onApprove, onReject, onDelete }) {
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [slotId, setSlotId] = useState('slot1');
  const [showApprove, setShowApprove] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const st = STATUS_STYLES[req.status] || STATUS_STYLES.pending_payment;
  const isExpired = req.expiresAt && new Date(req.expiresAt) < new Date();

  async function handleApprove() {
    setApproving(true);
    await onApprove(req.id, slotId);
    setApproving(false);
    setShowApprove(false);
  }

  async function handleReject() {
    setRejecting(true);
    await onReject(req.id);
    setRejecting(false);
  }

  async function handleDelete() {
    setDeleting(true);
    await onDelete(req.id);
    setDeleting(false);
  }

  return (
    <div className="bg-dark-700/50 border border-dark-600/50 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.bg} ${st.text}`}>{st.label}</span>
            <span className="text-xs text-white font-semibold">{req.advertiserName}</span>
            <span className="text-xs text-gray-500">{req.advertiserEmail}</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {req.slotType === 'header' ? 'Header Banner' : 'Sidebar Rectangle'} · {req.duration === 168 ? '7 days' : `${req.duration}h`} ·{' '}
            <span className="text-emerald-400 font-semibold">${req.amount}</span>
          </p>
          {req.adDetails?.headline && (
            <p className="text-xs text-gray-300 mt-0.5">"{req.adDetails.headline}"</p>
          )}
          {req.adDetails?.linkUrl && (
            <a href={req.adDetails.linkUrl} target="_blank" rel="noopener noreferrer"
              className="text-[10px] text-accent-primary flex items-center gap-1 mt-0.5 w-fit">
              <ExternalLink className="w-2.5 h-2.5" />{req.adDetails.linkUrl.slice(0, 40)}…
            </a>
          )}
          {req.expiresAt && req.status === 'approved' && (
            <div className="mt-1.5"><Countdown expiresAt={req.expiresAt} /></div>
          )}
        </div>
        <button onClick={() => setExpanded(e => !e)} className="text-gray-500 hover:text-gray-300 flex-shrink-0">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {expanded && req.adDetails && (
        <div className="mt-3 p-3 bg-dark-800 rounded-lg text-xs text-gray-400 space-y-1 border border-dark-600">
          {Object.entries(req.adDetails).map(([k, v]) => v ? (
            <div key={k} className="flex gap-2"><span className="text-gray-600 w-24 flex-shrink-0">{k}:</span><span className="text-gray-300">{v}</span></div>
          ) : null)}
          <div className="flex gap-2"><span className="text-gray-600 w-24 flex-shrink-0">Paid at:</span><span className="text-gray-300">{req.paidAt ? new Date(req.paidAt).toLocaleString() : '—'}</span></div>
        </div>
      )}

      {req.status === 'paid' && (
        <div className="mt-3 space-y-2">
          {showApprove ? (
            <div className="flex items-center gap-2">
              <select value={slotId} onChange={e => setSlotId(e.target.value)}
                className="flex-1 bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent-primary/50">
                {SLOT_OPTIONS.filter(s => req.slotType === 'header' ? s.id === 'header' : s.id !== 'header').map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
              <button onClick={handleApprove} disabled={approving}
                className="flex items-center gap-1.5 px-3 py-2 bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 rounded-lg text-xs font-semibold hover:bg-emerald-500/25 transition-colors disabled:opacity-50">
                <Check className="w-3.5 h-3.5" />{approving ? 'Activating…' : 'Approve & Go Live'}
              </button>
              <button onClick={() => setShowApprove(false)} className="p-2 text-gray-500 hover:text-gray-300">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => setShowApprove(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 rounded-lg text-xs font-semibold hover:bg-emerald-500/25 transition-colors">
                <Check className="w-3.5 h-3.5" /> Approve
              </button>
              <button onClick={handleReject} disabled={rejecting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-xs font-semibold hover:bg-red-500/20 transition-colors disabled:opacity-50">
                <X className="w-3.5 h-3.5" />{rejecting ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          )}
        </div>
      )}

      {req.status === 'pending_payment' && (
        <div className="mt-3">
          <button onClick={handleDelete} disabled={deleting}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-xs font-semibold hover:bg-red-500/20 transition-colors disabled:opacity-50">
            <Trash2 className="w-3.5 h-3.5" />{deleting ? 'Deleting…' : 'Delete (no payment received)'}
          </button>
        </div>
      )}

      {(req.status === 'rejected' || (req.status === 'approved' && isExpired)) && (
        <div className="mt-3">
          <button onClick={handleDelete} disabled={deleting}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-dark-600/60 border border-dark-500/40 text-gray-500 rounded-lg text-xs font-medium hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/20 transition-colors disabled:opacity-50">
            <Trash2 className="w-3 h-3" />{deleting ? 'Removing…' : 'Remove Record'}
          </button>
        </div>
      )}
    </div>
  );
}

function AdManagementTab({ onToast }) {
  const [subTab, setSubTab] = useState('requests');
  const [requests, setRequests] = useState(null);
  const [reqFilter, setReqFilter] = useState('all');
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  function loadRequests() {
    getAdRequests()
      .then(data => setRequests(data || []))
      .catch(() => setRequests([]));
  }

  function loadConfig() {
    getAdConfig()
      .then(data => setConfig(mergeAdConfig(data)))
      .catch(() => setConfig(DEFAULT_AD_CONFIG));
  }

  useEffect(() => { loadRequests(); loadConfig(); }, []);

  const paidCount = (requests || []).filter(r => r.status === 'paid').length;
  const unpaidCount = (requests || []).filter(r => r.status === 'pending_payment').length;
  const [deletingAll, setDeletingAll] = useState(false);

  async function handleApprove(id, slotId) {
    try {
      await approveAdRequest(id, slotId);
      onToast({ type: 'success', text: 'Ad approved and live! Timer has started.' });
      loadRequests(); loadConfig();
    } catch (e) {
      onToast({ type: 'error', text: e.message || 'Approve failed' });
    }
  }

  async function handleReject(id) {
    try {
      await rejectAdRequest(id);
      onToast({ type: 'success', text: 'Request rejected. Process refund via Stripe dashboard if needed.' });
      loadRequests();
    } catch (e) {
      onToast({ type: 'error', text: e.message || 'Reject failed' });
    }
  }

  async function handleDeleteAllUnpaid() {
    const targets = (requests || []).filter(r => r.status === 'pending_payment');
    if (targets.length === 0) return;
    setDeletingAll(true);
    try {
      await Promise.all(targets.map(r => deleteAdRequest(r.id)));
      onToast({ type: 'success', text: `Deleted ${targets.length} unpaid request${targets.length > 1 ? 's' : ''}.` });
      loadRequests();
    } catch (e) {
      onToast({ type: 'error', text: e.message || 'Bulk delete failed' });
    } finally {
      setDeletingAll(false);
    }
  }

  async function handlePurgeExpired() {
    const now = new Date();
    const targets = (requests || []).filter(r =>
      r.status === 'rejected' ||
      (r.status === 'approved' && r.expiresAt && new Date(r.expiresAt) < now)
    );
    if (targets.length === 0) return;
    setDeletingAll(true);
    try {
      await Promise.all(targets.map(r => deleteAdRequest(r.id)));
      onToast({ type: 'success', text: `Purged ${targets.length} expired/rejected record${targets.length > 1 ? 's' : ''}.` });
      loadRequests();
    } catch (e) {
      onToast({ type: 'error', text: e.message || 'Purge failed' });
    } finally {
      setDeletingAll(false);
    }
  }

  async function handleDelete(id) {
    try {
      await deleteAdRequest(id);
      onToast({ type: 'success', text: 'Record removed.' });
      loadRequests();
    } catch (e) {
      onToast({ type: 'error', text: e.message || 'Delete failed' });
    }
  }

  function setHeader(field, val) {
    setConfig(prev => ({ ...prev, header: { ...prev.header, [field]: val } }));
  }

  function setSlot(idx, field, val) {
    setConfig(prev => {
      const slots = [...prev.slots];
      slots[idx] = { ...slots[idx], [field]: val };
      return { ...prev, slots };
    });
  }

  async function handleSave() {
    setSaving(true);
    try {
      await saveAdConfig(config);
      onToast({ type: 'success', text: 'Ad configuration saved — live immediately' });
      loadConfig();
    } catch (e) {
      onToast({ type: 'error', text: e.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  }

  const filteredRequests = (requests || []).filter(r =>
    reqFilter === 'all' || r.status === reqFilter
  );

  // Active slots with expiry
  const activeSlots = config ? [
    ...(config.header?.enabled ? [{ label: 'Header Banner', ...config.header }] : []),
    ...(config.slots || []).filter(s => s.enabled).map((s, i) => ({ label: `Sidebar Slot ${s.id.replace('slot','')}`, ...s })),
  ] : [];

  return (
    <div className="space-y-6">
      {/* Sub-tab navigation */}
      <div className="flex gap-1 bg-dark-700/50 p-1 rounded-xl w-fit">
        {[
          { id: 'requests', label: 'Ad Requests', badge: paidCount + unpaidCount },
          { id: 'manual',   label: 'Manual Placement' },
        ].map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              subTab === t.id ? 'bg-accent-primary text-dark-900' : 'text-gray-400 hover:text-white'
            }`}>
            {t.label}
            {t.badge > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${subTab === t.id ? 'bg-dark-900/30 text-dark-900' : 'bg-amber-500 text-dark-900'}`}>
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Requests tab ── */}
      {subTab === 'requests' && (
        <div className="space-y-5">

          {/* Active ads with countdown */}
          {activeSlots.length > 0 && (
            <div className="bg-dark-800 border border-dark-700/50 rounded-2xl p-5">
              <h3 className="font-semibold text-white text-sm mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-emerald-400" /> Live Ads
              </h3>
              <div className="space-y-2">
                {activeSlots.map((s, i) => (
                  <div key={i} className="flex items-center justify-between bg-dark-700/50 rounded-lg px-3 py-2.5 text-xs">
                    <div>
                      <span className="text-white font-medium">{s.label}</span>
                      {s.headline && <span className="text-gray-500 ml-2">"{s.headline}"</span>}
                    </div>
                    {s.expiresAt
                      ? <Countdown expiresAt={s.expiresAt} />
                      : <span className="text-gray-500 text-xs">No expiry (manual)</span>
                    }
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Request list */}
          <div className="bg-dark-800 border border-dark-700/50 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <h3 className="font-semibold text-white text-sm">All Requests</h3>
              <div className="flex items-center gap-2 flex-wrap">
                {unpaidCount > 0 && (
                  <button onClick={handleDeleteAllUnpaid} disabled={deletingAll}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-xs font-semibold hover:bg-red-500/20 transition-colors disabled:opacity-50">
                    <Trash2 className="w-3 h-3" />{deletingAll ? 'Deleting…' : `Delete All Unpaid (${unpaidCount})`}
                  </button>
                )}
                {(requests || []).some(r => r.status === 'rejected' || (r.status === 'approved' && r.expiresAt && new Date(r.expiresAt) < new Date())) && (
                  <button onClick={handlePurgeExpired} disabled={deletingAll}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-dark-600/60 border border-dark-500/40 text-gray-500 rounded-lg text-xs font-medium hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/20 transition-colors disabled:opacity-50">
                    <Trash2 className="w-3 h-3" />{deletingAll ? 'Purging…' : 'Purge Expired & Rejected'}
                  </button>
                )}
                <div className="flex gap-1">
                  {[
                    { id: 'all',             label: 'All'     },
                    { id: 'paid',            label: 'Review'  },
                    { id: 'approved',        label: 'Live'    },
                    { id: 'rejected',        label: 'Rejected'},
                    { id: 'pending_payment', label: 'Unpaid'  },
                  ].map(f => (
                    <button key={f.id} onClick={() => setReqFilter(f.id)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                        reqFilter === f.id ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30' : 'text-gray-500 hover:text-gray-300'
                      }`}>
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {requests === null ? (
              <div className="flex justify-center py-8"><Spinner /></div>
            ) : filteredRequests.length === 0 ? (
              <div className="text-center py-8">
                <Megaphone className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No {reqFilter !== 'all' ? reqFilter : ''} requests yet</p>
                <p className="text-xs text-gray-600 mt-1">Share <span className="text-accent-primary">haythix.com/advertise</span> to attract advertisers</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredRequests.map(req => (
                  <AdRequestCard key={req.id} req={req} onApprove={handleApprove} onReject={handleReject} onDelete={handleDelete} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Manual Placement tab ── */}
      {subTab === 'manual' && config && (
        <div className="space-y-6">
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-4 flex items-start gap-3">
            <Megaphone className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-gray-400 space-y-0.5">
              <p className="text-amber-400 font-medium">Manual / Admin Override</p>
              <p>Place ads directly without payment — for partners, friends, or test content.</p>
              <p>Set an optional expiry so ads turn off automatically. Leave blank for permanent placement.</p>
              <p>Self-serve public page: <span className="text-white">/advertise</span> · Specs: Header 728×90 from $500/day · Sidebar 300×250 from $300/day</p>
            </div>
          </div>

          {/* Header Ad */}
          <SectionCard title="Header Banner" subtitle="Leaderboard · 728×90 · center of top bar" icon={Image}
            action={<Toggle enabled={config.header.enabled} onChange={v => setHeader('enabled', v)} />}>
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${config.header.enabled ? 'bg-emerald-500/15 text-emerald-400' : 'bg-dark-600 text-gray-500'}`}>
                  {config.header.enabled ? 'Active' : 'Disabled'}
                </span>
                {config.header.expiresAt && <Countdown expiresAt={config.header.expiresAt} />}
              </div>
              <ImageUploadField label="Banner Image (optional — leave blank for text card)" value={config.header.imageUrl} onChange={v => setHeader('imageUrl', v)} specs="728×90 px · PNG, JPG, GIF, WebP · max 3MB" />
              <UrlField label="Destination Link" value={config.header.linkUrl} onChange={v => setHeader('linkUrl', v)} />
              <div className="grid grid-cols-2 gap-3">
                <AdField label="Headline" value={config.header.headline} onChange={v => setHeader('headline', v)} placeholder="Advertise Here" />
                <AdField label="CTA Button Text" value={config.header.cta} onChange={v => setHeader('cta', v)} placeholder="Learn More" />
              </div>
              <AdField label="Sub-text" value={config.header.subText} onChange={v => setHeader('subText', v)} placeholder="Reach thousands of active DeFi traders" />
              <div>
                <label className="flex items-center gap-1.5 text-xs text-gray-400 mb-1"><Calendar className="w-3 h-3" /> Auto-expire at (optional)</label>
                <input type="datetime-local" value={config.header.expiresAt ? new Date(config.header.expiresAt).toISOString().slice(0,16) : ''}
                  onChange={e => setHeader('expiresAt', e.target.value ? new Date(e.target.value).toISOString() : null)}
                  className="bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent-primary/50" />
              </div>
            </div>
          </SectionCard>

          {/* Sidebar Slots */}
          {config.slots.map((slot, idx) => {
            const c = COLOR_MAP[slot.accentColor] || COLOR_MAP.amber;
            return (
              <SectionCard key={slot.id} title={`Sidebar Slot ${idx + 1}`} subtitle="Medium Rectangle · 300×250 · right column" icon={Image}
                action={<Toggle enabled={slot.enabled} onChange={v => setSlot(idx, 'enabled', v)} />}>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${slot.enabled ? 'bg-emerald-500/15 text-emerald-400' : 'bg-dark-600 text-gray-500'}`}>
                      {slot.enabled ? 'Active' : 'Disabled'}
                    </span>
                    {slot.expiresAt && <Countdown expiresAt={slot.expiresAt} />}
                    <span className="text-xs text-gray-500">Accent:</span>
                    <div className="flex gap-1.5">
                      {ACCENT_COLORS.map(col => {
                        const cm = COLOR_MAP[col];
                        return (
                          <button key={col} onClick={() => setSlot(idx, 'accentColor', col)}
                            className={`w-5 h-5 rounded-full border-2 transition-all ${cm.bg} ${slot.accentColor === col ? cm.border + ' scale-125' : 'border-transparent'}`}
                            title={col} />
                        );
                      })}
                    </div>
                  </div>
                  <ImageUploadField label="Banner Image (optional)" value={slot.imageUrl} onChange={v => setSlot(idx, 'imageUrl', v)} specs="300×250 px · PNG, JPG, GIF, WebP · max 3MB" />
                  <UrlField label="Destination Link" value={slot.linkUrl} onChange={v => setSlot(idx, 'linkUrl', v)} />
                  <div className="grid grid-cols-2 gap-3">
                    <AdField label="Tag (e.g. Sponsored)" value={slot.tag} onChange={v => setSlot(idx, 'tag', v)} placeholder="Sponsored" />
                    <AdField label="CTA Button Text" value={slot.cta} onChange={v => setSlot(idx, 'cta', v)} placeholder="Learn More" />
                  </div>
                  <AdField label="Headline" value={slot.headline} onChange={v => setSlot(idx, 'headline', v)} placeholder="Your Ad Here" />
                  <AdField label="Body Text" value={slot.body} onChange={v => setSlot(idx, 'body', v)} placeholder="300×250 · Premium placement" />

                  <div>
                    <label className="flex items-center gap-1.5 text-xs text-gray-400 mb-1"><Calendar className="w-3 h-3" /> Auto-expire at (optional)</label>
                    <input type="datetime-local" value={slot.expiresAt ? new Date(slot.expiresAt).toISOString().slice(0,16) : ''}
                      onChange={e => setSlot(idx, 'expiresAt', e.target.value ? new Date(e.target.value).toISOString() : null)}
                      className="bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent-primary/50" />
                  </div>

                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mb-2">Preview</p>
                    <div className={`rounded-xl border p-4 max-w-[200px] ${c.bg} ${c.border}`}>
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${c.bg} ${c.text} mb-2 inline-block`}>{slot.tag || 'Sponsored'}</span>
                      <p className="text-white text-xs font-semibold leading-tight">{slot.headline || 'Headline'}</p>
                      <p className="text-gray-400 text-[10px] mt-1">{slot.body || 'Body text'}</p>
                      <div className={`mt-2 text-[10px] font-medium ${c.text}`}>{slot.cta || 'CTA'} →</div>
                    </div>
                  </div>
                </div>
              </SectionCard>
            );
          })}

          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-accent-primary text-dark-900 rounded-xl font-semibold text-sm hover:bg-accent-primary/90 transition-colors disabled:opacity-50">
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save & Publish Ads'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Users ───────────────────────────────────────────────────────────────

const TIER_COLORS = {
  // 2026-07-30: `pending` was missing from this map + the tier breakdown
  // cards, so users in the pending-payment gate (introduced 2026-05-29 —
  // see project_pending_tier_gate) weren't counted. Result: Admin Center
  // showed 10 users while there were actually 15.
  pending:  'text-amber-300 bg-amber-500/10 border-amber-500/30',
  trial:    'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  starter:  'text-blue-400 bg-blue-500/10 border-blue-500/30',
  trader:   'text-cyan-400 bg-cyan-500/10 border-cyan-500/30',
  pro:      'text-violet-400 bg-violet-500/10 border-violet-500/30',
  elite:    'text-amber-400 bg-amber-400/10 border-amber-400/30',
};

function UsersTab({ showToast }) {
  const { runWithStepUp } = useStepUpGate();
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [tierFilter,   setTierFilter]   = useState('all');
  const [actionUserId, setActionUserId] = useState(null);
  const [resetModal,   setResetModal]   = useState(null); // { userId, email }
  const [tierModal,    setTierModal]    = useState(null); // { userId, email, currentTier }
  const [newPassword,  setNewPassword]  = useState('');
  const [newTier,      setNewTier]      = useState('');
  const [cancelStripe, setCancelStripe] = useState(false);
  const [saving,       setSaving]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await adminListUsers()); } catch { setData(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = (data?.users || []).filter(u => {
    const matchSearch = !search || u.email.toLowerCase().includes(search.toLowerCase()) || (u.name || '').toLowerCase().includes(search.toLowerCase());
    const matchTier   = tierFilter === 'all' || tierFilter === 'complimentary' ? (tierFilter === 'complimentary' ? !!u.complimentaryTier : true) : u.tier === tierFilter;
    return matchSearch && matchTier;
  });

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) return;
    setSaving(true);
    try {
      await adminResetPassword(resetModal.userId, newPassword);
      showToast(`Password reset for ${resetModal.email}`);
      setResetModal(null); setNewPassword('');
    } catch (e) { showToast(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };

  const handleChangeTier = async () => {
    if (!newTier) return;
    setSaving(true);
    try {
      await adminChangeTier(tierModal.userId, newTier, cancelStripe);
      showToast(`${tierModal.email} moved to ${newTier}${cancelStripe ? ' — free forever 🎁' : ''}`);
      setTierModal(null); setNewTier(''); setCancelStripe(false);
      load();
    } catch (e) { showToast(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(null); // user object to delete

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setActionUserId(deleteConfirm.userId);
    try {
      // runWithStepUp catches STEPUP_REQUIRED, opens the TOTP modal, and
      // retries the call automatically once the admin proves a fresh credential.
      // Without this wrapper, the first delete-attempt just errors with
      // "Fresh authentication required" and the user has no way to satisfy it.
      await runWithStepUp(
        () => adminDeleteUser(deleteConfirm.userId),
        `Delete user account (${deleteConfirm.email})`
      );
      showToast(`${deleteConfirm.email} deleted`);
      setDeleteConfirm(null);
      load();
    } catch (e) {
      if (e.code === 'APPROVAL_REQUIRED') {
        showToast(`Approval requested — see Approvals tab to track #${e.approvalId?.slice(0, 8)}`);
        setDeleteConfirm(null);
      } else { showToast(`Error: ${e.message}`); }
    }
    finally { setActionUserId(null); }
  };

  const handleSuspend = async (u) => {
    setActionUserId(u.userId);
    try {
      await adminSuspendUser(u.userId, !u.suspended);
      showToast(u.suspended ? `${u.email} unsuspended` : `${u.email} suspended`);
      load();
    } catch (e) { showToast(`Error: ${e.message}`); }
    finally { setActionUserId(null); }
  };

  const tc = data?.tierCounts || {};

  return (
    <div className="space-y-6">
      {/* Tier breakdown */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          // `pending` = signed up but hasn't cleared Stripe Checkout yet.
          // Rendered first so admins see the top-of-funnel gap at a glance.
          { tier: 'pending', label: 'Pending', color: 'text-amber-300',   bg: 'bg-amber-500/10'   },
          { tier: 'trial',   label: 'Trial',   color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
          { tier: 'starter', label: 'Starter', color: 'text-blue-400',    bg: 'bg-blue-500/10'    },
          { tier: 'trader',  label: 'Trader',  color: 'text-cyan-400',    bg: 'bg-cyan-500/10'    },
          { tier: 'pro',     label: 'Pro',     color: 'text-violet-400',  bg: 'bg-violet-500/10'  },
          { tier: 'elite',   label: 'Elite',   color: 'text-amber-400',   bg: 'bg-amber-400/10'   },
        ].map(({ tier, label, color, bg }) => (
          <button key={tier} onClick={() => setTierFilter(t => t === tier ? 'all' : tier)}
            className={`${bg} border ${TIER_COLORS[tier].split(' ').slice(2).join(' ')} rounded-2xl p-5 text-left transition-all hover:opacity-80 ${tierFilter === tier ? 'ring-2 ring-offset-2 ring-offset-dark-900 ring-current' : ''}`}>
            <p className={`text-3xl font-bold ${color}`}>{tc[tier] ?? '—'}</p>
            <p className="text-gray-400 text-sm mt-1">{label} users</p>
          </button>
        ))}
      </div>

      {/* Search + filter bar */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="input pl-9 w-full" />
        </div>
        <select value={tierFilter} onChange={e => setTierFilter(e.target.value)}
          className="input w-44 bg-dark-800">
          <option value="all">All tiers</option>
          <option value="pending">Pending</option>
          <option value="trial">Trial</option>
          <option value="starter">Starter</option>
          <option value="trader">Trader</option>
          <option value="pro">Pro</option>
          <option value="elite">Elite</option>
          <option value="complimentary">Complimentary</option>
        </select>
        <button onClick={load} className="btn-secondary flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* User table */}
      <div className="bg-dark-800 rounded-2xl border border-dark-700/50 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40"><Spinner size="lg" className="text-accent-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500">No users found</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-700/50 text-gray-500 text-xs uppercase tracking-wide">
                <th className="text-left px-5 py-3">User</th>
                <th className="text-left px-5 py-3">Tier</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Joined</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700/30">
              {filtered.map(u => (
                <tr key={u.userId} className={`hover:bg-dark-700/30 transition-colors ${u.suspended ? 'opacity-50' : ''}`}>
                  <td className="px-5 py-3">
                    <p className="text-white font-medium">{u.name || '—'}</p>
                    <p className="text-gray-500 text-xs">{u.email}</p>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${TIER_COLORS[u.tier] || TIER_COLORS.trial}`}>
                        {u.tier}
                      </span>
                      {u.complimentaryTier && (
                        <span title={`Gifted from ${u.complimentaryFrom} on ${new Date(u.complimentaryGrantedAt).toLocaleDateString()} by ${u.complimentaryGrantedBy}`}
                          className="text-xs px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-400 border border-violet-500/30 cursor-help">
                          🎁 Gift
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    {u.suspended ? (
                      <span className="text-xs text-red-400">Suspended</span>
                    ) : u.subscriptionStatus ? (
                      <span className="text-xs text-gray-400">{u.subscriptionStatus}</span>
                    ) : (
                      <span className="text-xs text-gray-600">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-gray-500 text-xs">
                    {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => { setTierModal({ userId: u.userId, email: u.email, currentTier: u.tier }); setNewTier(u.tier); }}
                        className="text-xs px-2 py-1 rounded-lg bg-accent-primary/10 text-accent-primary hover:bg-accent-primary/20 transition-colors">
                        Change Tier
                      </button>
                      <button onClick={() => { setResetModal({ userId: u.userId, email: u.email }); setNewPassword(''); }}
                        className="text-xs px-2 py-1 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors">
                        Reset PWD
                      </button>
                      <button onClick={() => handleSuspend(u)} disabled={actionUserId === u.userId}
                        className={`text-xs px-2 py-1 rounded-lg transition-colors ${u.suspended ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' : 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'}`}>
                        {actionUserId === u.userId ? '…' : u.suspended ? 'Unsuspend' : 'Suspend'}
                      </button>
                      <button onClick={() => setDeleteConfirm(u)} disabled={actionUserId === u.userId}
                        className="text-xs px-2 py-1 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Reset Password Modal */}
      {resetModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 border border-dark-600 rounded-2xl p-6 w-full max-w-sm space-y-4">
            <h3 className="text-white font-semibold">Reset Password</h3>
            <p className="text-gray-400 text-sm">{resetModal.email}</p>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
              placeholder="New password (min 8 chars)" className="input w-full" autoFocus />
            <div className="flex gap-3">
              <button onClick={() => setResetModal(null)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={handleResetPassword} disabled={saving || newPassword.length < 8}
                className="btn-primary flex-1">{saving ? 'Saving…' : 'Reset'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Change Tier Modal */}
      {tierModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 border border-dark-600 rounded-2xl p-6 w-full max-w-sm space-y-4">
            <h3 className="text-white font-semibold">Change Subscription Tier</h3>
            <p className="text-gray-400 text-sm">{tierModal.email}</p>
            <select value={newTier} onChange={e => setNewTier(e.target.value)} className="input w-full bg-dark-700">
              <option value="trial">Trial</option>
              <option value="starter">Starter — $10/mo</option>
              <option value="trader">Trader — $49.99/mo</option>
              <option value="pro">Pro — $75.99/mo</option>
              <option value="elite">Elite — $149.99/mo</option>
            </select>
            <label className="flex items-start gap-3 p-3 rounded-xl bg-dark-700/50 border border-dark-600 cursor-pointer hover:border-violet-500/40 transition-colors">
              <input type="checkbox" checked={cancelStripe} onChange={e => setCancelStripe(e.target.checked)}
                className="mt-0.5 accent-violet-500" />
              <div>
                <p className="text-sm text-white font-medium">Cancel their Stripe subscription</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  They won't be charged going forward — fully complimentary gift.
                  {tierModal.hasStripe ? '' : ' (No active Stripe subscription found — system tier only.)'}
                </p>
              </div>
            </label>
            {!cancelStripe && (
              <p className="text-xs text-amber-400/80">
                System tier only — Stripe keeps billing at their current price.
              </p>
            )}
            <div className="flex gap-3">
              <button onClick={() => { setTierModal(null); setCancelStripe(false); }} className="btn-secondary flex-1">Cancel</button>
              <button onClick={handleChangeTier} disabled={saving || newTier === tierModal.currentTier}
                className="btn-primary flex-1">{saving ? 'Saving…' : 'Apply'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 border border-red-500/30 rounded-2xl p-6 w-full max-w-sm space-y-4">
            <h3 className="text-white font-semibold">Delete User</h3>
            <p className="text-gray-400 text-sm">
              Permanently delete <span className="text-white font-medium">{deleteConfirm.email}</span>?
              This removes their account and all associated data. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={handleDelete} disabled={actionUserId === deleteConfirm.userId}
                className="flex-1 px-4 py-2 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500/30 font-medium transition-colors">
                {actionUserId === deleteConfirm.userId ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Paid Channels Command Center (KPIs + sortable + drill-down) ────────────

function PaidChannelsCommandCenter({ channels = [], kpis, onRefresh, onChannelClick }) {
  const [sortKey, setSortKey] = useState('activeSubs'); // activeSubs | mrr | totalEarnings | activatedAt

  const sorted = [...channels].sort((a, b) => {
    if (sortKey === 'activatedAt') return new Date(b.activatedAt || 0) - new Date(a.activatedAt || 0);
    return (b[sortKey] || 0) - (a[sortKey] || 0);
  });

  return (
    <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Crown className="w-5 h-5 text-amber-400" /> Paid Channels Command Center
        </h3>
        <button onClick={onRefresh} className="text-[10px] text-gray-500 hover:text-white">Refresh</button>
      </div>

      {/* KPI strip */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Active channels</p>
            <p className="text-2xl font-bold text-white">{kpis.totalChannels}</p>
            <p className="text-[10px] text-gray-500">{kpis.totalPendingSubs} pending subs</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-xl p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Active subscribers</p>
            <p className="text-2xl font-bold text-emerald-400">{kpis.totalActiveSubs}</p>
            <p className="text-[10px] text-gray-500">{kpis.totalCancelledSubs} cancelled lifetime</p>
          </div>
          <div className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border border-violet-500/20 rounded-xl p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">MRR (Monthly recurring)</p>
            <p className="text-2xl font-bold text-violet-400">${kpis.totalMRR.toFixed(2)}</p>
            <p className="text-[10px] text-gray-500">Across all active subs</p>
          </div>
          <div className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border border-amber-500/20 rounded-xl p-3">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Platform 20% lifetime</p>
            <p className="text-2xl font-bold text-amber-400">${kpis.totalPlatformLifetime.toFixed(2)}</p>
            <p className="text-[10px] text-gray-500">Creator paid: ${kpis.totalCreatorPaidLifetime.toFixed(2)}</p>
          </div>
        </div>
      )}

      {channels.length === 0 ? (
        <p className="text-gray-600 text-sm text-center py-6">No active paid channels yet. Approve a creator's application to get started.</p>
      ) : (
        <>
          {/* Sort controls */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] text-gray-500 uppercase tracking-wider">Sort:</span>
            {[
              { key: 'activeSubs', label: 'Busiest' },
              { key: 'mrr', label: 'Highest MRR' },
              { key: 'totalEarnings', label: 'Lifetime $' },
              { key: 'activatedAt', label: 'Newest' },
            ].map(s => (
              <button key={s.key} onClick={() => setSortKey(s.key)}
                className={`text-[10px] font-bold px-2 py-1 rounded-md transition-all ${
                  sortKey === s.key ? 'bg-amber-500/20 border border-amber-500/40 text-amber-300' : 'bg-dark-700 border border-dark-600 text-gray-400 hover:text-white'
                }`}>
                {s.label}
              </button>
            ))}
          </div>

          <div className="space-y-2">
            {sorted.map(ch => (
              <button key={ch.channelId} onClick={() => onChannelClick(ch.channelId)}
                className="w-full bg-dark-700/30 hover:bg-dark-700/60 rounded-xl p-4 border border-dark-600/30 hover:border-amber-500/30 transition-all text-left">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <code className="text-sm font-bold text-amber-400">#{ch.channelId}</code>
                      {ch.activeSubs > 0 && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">ACTIVE</span>
                      )}
                      {ch.activeSubs === 0 && ch.totalEarnings > 0 && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">DORMANT</span>
                      )}
                      {ch.activeSubs === 0 && ch.totalEarnings === 0 && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-gray-500/20 text-gray-400">NEW</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 truncate">{ch.description || 'No description'}</p>
                    <p className="text-[10px] text-gray-500 mt-1">
                      Creator: <span className="text-white font-medium">{ch.creatorName || ch.creatorUserId?.slice(0, 8)}</span>
                      {ch.creatorPayoutWallet ? (
                        <span className="ml-2 font-mono">{ch.creatorPayoutWallet.slice(0, 6)}...{ch.creatorPayoutWallet.slice(-4)}</span>
                      ) : <span className="ml-2 text-red-400">NO WALLET</span>}
                    </p>
                  </div>
                  <div className="text-right text-[10px] text-gray-500 flex-shrink-0">
                    Activated<br />
                    <span className="text-gray-400">{ch.activatedAt ? new Date(ch.activatedAt).toLocaleDateString() : '—'}</span>
                  </div>
                </div>

                <div className="grid grid-cols-5 gap-3 pt-2 border-t border-dark-600/30">
                  <div>
                    <p className="text-[9px] text-gray-500 uppercase">Subs</p>
                    <p className="text-base font-bold text-emerald-400">{ch.activeSubs}</p>
                    {ch.pendingSubs > 0 && <p className="text-[9px] text-amber-400">{ch.pendingSubs} pending</p>}
                  </div>
                  <div>
                    <p className="text-[9px] text-gray-500 uppercase">MRR</p>
                    <p className="text-base font-bold text-violet-400">${ch.mrr.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-gray-500 uppercase">Creator earned</p>
                    <p className="text-base font-bold text-white">${ch.totalEarnings.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-gray-500 uppercase">Your 20%</p>
                    <p className="text-base font-bold text-amber-400">${ch.totalPlatformFees.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-gray-500 uppercase">Churn</p>
                    <p className={`text-base font-bold ${ch.churnRate > 30 ? 'text-red-400' : ch.churnRate > 15 ? 'text-amber-400' : 'text-emerald-400'}`}>{ch.churnRate}%</p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-1 mt-2">
                  {ch.tiers.map((t, i) => (
                    <span key={i} className="text-[10px] bg-dark-800 text-gray-400 px-2 py-0.5 rounded border border-dark-600">
                      {t.name} <span className="text-amber-400">${t.price}/mo</span>
                    </span>
                  ))}
                  <span className="ml-auto text-[10px] text-amber-400 font-bold">Click to drill down →</span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Per-channel drill-down modal ──────────────────────────────────────────

function PaidChannelDrilldown({ channelId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getPaidChannelDetail(channelId)
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [channelId]);

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-dark-800 rounded-2xl border border-dark-700 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-dark-800 border-b border-dark-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Channel: <code className="text-amber-400">#{channelId}</code></h2>
            {data?.channel?.creatorName && (
              <p className="text-[11px] text-gray-500 mt-0.5">Creator: {data.channel.creatorName} ({data.channel.creatorEmail || data.channel.creatorUserId?.slice(0, 8)})</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-2xl leading-none">×</button>
        </div>

        <div className="p-6 space-y-5">
          {loading && <div className="flex justify-center py-12"><Spinner /></div>}
          {error && <p className="text-red-400 text-sm">{error}</p>}

          {data?.channel && (
            <>
              <div className="bg-dark-700/30 rounded-xl p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Description</p>
                <p className="text-sm text-white">{data.channel.description || '—'}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-dark-600/30">
                  <div><p className="text-[10px] text-gray-500">Creator earned</p><p className="text-sm font-bold text-emerald-400">${data.channel.totalEarnings.toFixed(2)}</p></div>
                  <div><p className="text-[10px] text-gray-500">Platform 20%</p><p className="text-sm font-bold text-amber-400">${data.channel.totalPlatformFees.toFixed(2)}</p></div>
                  <div><p className="text-[10px] text-gray-500">Tiers</p><p className="text-sm font-bold text-white">{data.channel.tiers?.length || 0}</p></div>
                  <div><p className="text-[10px] text-gray-500">Activated</p><p className="text-xs text-gray-400">{data.channel.activatedAt ? new Date(data.channel.activatedAt).toLocaleDateString() : '—'}</p></div>
                </div>
                <div className="mt-3 pt-3 border-t border-dark-600/30">
                  <p className="text-[10px] text-gray-500 mb-1">Creator payout wallet</p>
                  <p className="text-xs font-mono text-white">{data.channel.creatorPayoutWallet || <span className="text-red-400">NOT SET</span>}</p>
                </div>
                {data.channel.tiers?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-dark-600/30">
                    <p className="text-[10px] text-gray-500 mb-2">Subscription tiers</p>
                    <div className="space-y-1.5">
                      {data.channel.tiers.map((t, i) => (
                        <div key={i} className="bg-dark-800 rounded-lg p-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-white">{t.name}</span>
                            <span className="text-sm font-bold text-amber-400">${t.price}/mo</span>
                          </div>
                          {t.perks && t.perks.length > 0 && (
                            <ul className="mt-1 pl-3 text-[10px] text-gray-500 list-disc">
                              {t.perks.map((p, j) => <li key={j}>{p}</li>)}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-bold text-white mb-2">Subscribers ({data.subscribers?.length || 0})</h3>
                {data.subscribers?.length === 0 ? (
                  <p className="text-xs text-gray-500">No subscribers yet.</p>
                ) : (
                  <div className="space-y-2">
                    {data.subscribers.map(sub => (
                      <div key={sub.subscriptionId} className="bg-dark-700/30 rounded-xl p-3 border border-dark-600/30">
                        <div className="flex items-start justify-between mb-2 gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white"><span className="font-bold">{sub.subscriberName || sub.subscriberUserId?.slice(0, 8)}</span> <span className="text-[10px] text-gray-500 ml-1">{sub.tierName}</span></p>
                            <p className="text-[10px] text-gray-500 mt-0.5 font-mono">{sub.subscriberWallet ? `${sub.subscriberWallet.slice(0, 8)}...${sub.subscriberWallet.slice(-6)}` : '—'}</p>
                          </div>
                          <div className="text-right text-[10px] flex-shrink-0">
                            <span className={`font-bold px-1.5 py-0.5 rounded ${
                              sub.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                              sub.status === 'pending_payment' ? 'bg-amber-500/20 text-amber-400' :
                              sub.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>{sub.status.toUpperCase()}</span>
                            {sub.autoRenew && <p className="text-[9px] text-cyan-400 mt-0.5">auto-renew on</p>}
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-[10px] border-t border-dark-600/30 pt-2">
                          <div><p className="text-gray-500">Started</p><p className="text-white">{sub.startedAt ? new Date(sub.startedAt).toLocaleDateString() : '—'}</p></div>
                          <div><p className="text-gray-500">Expires</p><p className="text-white">{sub.expiresAt ? new Date(sub.expiresAt).toLocaleDateString() : '—'}</p></div>
                          <div><p className="text-gray-500">Payments</p><p className="text-white">{sub.paymentsCount}</p></div>
                        </div>
                        {sub.payments?.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-dark-600/30 space-y-1">
                            {sub.payments.slice(0, 3).map((p, i) => (
                              <div key={i} className="flex items-center justify-between text-[10px]">
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-500">{new Date(p.paidAt).toLocaleDateString()}</span>
                                  <span className="text-white">${p.amount} on {p.chain || 'base'}</span>
                                  {p.source === 'auto_renew' && <span className="text-cyan-400">[auto]</span>}
                                </div>
                                {p.txHash && (
                                  <a href={`https://${p.chain === 'polygon' ? 'polygonscan.com' : p.chain === 'arbitrum' ? 'arbiscan.io' : 'basescan.org'}/tx/${p.txHash}`}
                                    target="_blank" rel="noopener"
                                    className="text-amber-400 hover:text-amber-300 font-mono">
                                    {p.txHash.slice(0, 6)}...↗
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Paid Channels Admin Tab ────────────────────────────────────────────────

function PaidChannelsAdminTab({ onToast }) {
  const [flags, setFlags] = useState(null);
  const [allowlist, setAllowlist] = useState([]);
  const [pending, setPending] = useState([]);
  const [failedPayouts, setFailedPayouts] = useState([]);
  const [retrying, setRetrying] = useState(null);
  const [treasuryStatus, setTreasuryStatus] = useState(null);
  const [activeChannels, setActiveChannels] = useState({ channels: [], kpis: null });
  const [drilldownChannelId, setDrilldownChannelId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [newAllowlistUser, setNewAllowlistUser] = useState('');

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const [flagData, pendingData, failedData, treasuryData, activeData] = await Promise.all([
        getPaidChannelFlags(),
        getPaidChannelPending(),
        getPaidChannelFailedPayouts().catch(() => ({ failed: [] })),
        getPaidChannelTreasuryStatus().catch(() => null),
        getPaidChannelActiveChannels().catch(() => ({ channels: [] })),
      ]);
      setFlags(flagData.flags || {});
      setAllowlist(flagData.allowlist || []);
      setPending(pendingData.applications || []);
      setFailedPayouts(failedData.failed || []);
      setTreasuryStatus(treasuryData);
      setActiveChannels({ channels: activeData.channels || [], kpis: activeData.kpis || null });
    } catch (err) {
      onToast({ type: 'error', text: err?.message || 'Failed to load paid-channel admin data' });
    }
    setLoading(false);
  }

  async function retryPayout(subscriptionId, paymentId) {
    setRetrying(`${subscriptionId}:${paymentId}`);
    try {
      const res = await retryPaidChannelPayout(subscriptionId, paymentId);
      if (res?.success) {
        onToast({ type: 'success', text: `Payout sent · tx ${res.txHash?.slice(0, 10)}...` });
        loadData();
      } else {
        onToast({ type: 'error', text: res?.error || res?.reason || 'Retry failed' });
      }
    } catch (err) { onToast({ type: 'error', text: err.message }); }
    setRetrying(null);
  }

  async function toggleFlag(flagName) {
    setToggling(flagName);
    try {
      const newValue = !flags[flagName];
      const res = await togglePaidChannelFlag(flagName, newValue);
      setFlags(res.flags);
      onToast({ type: 'success', text: `${flagName} is now ${newValue ? 'ON' : 'OFF'}` });
    } catch (err) { onToast({ type: 'error', text: err.message }); }
    setToggling(null);
  }

  async function addToAllowlist() {
    if (!newAllowlistUser.trim()) return;
    try {
      const res = await managePaidChannelAllowlist('add', newAllowlistUser.trim());
      setAllowlist(res.allowlist || []);
      setNewAllowlistUser('');
      onToast({ type: 'success', text: 'User added to allowlist' });
    } catch (err) { onToast({ type: 'error', text: err.message }); }
  }

  async function approveApp(applicationId) {
    try {
      const data = await approvePaidChannelApp(applicationId, 'Approved by admin');
      onToast({ type: 'success', text: `Approved — channel ${data.channelId} is now paid` });
      await loadData();
    } catch (err) { onToast({ type: 'error', text: `Approve failed: ${err.message}` }); }
  }

  async function rejectApp(applicationId) {
    try {
      await rejectPaidChannelApp(applicationId, 'Does not meet requirements');
      onToast({ type: 'success', text: 'Application rejected' });
      await loadData();
    } catch (err) { onToast({ type: 'error', text: `Reject failed: ${err.message}` }); }
  }

  if (loading) return <div className="flex justify-center py-12"><Spinner /></div>;

  return (
    <div className="space-y-6">
      {/* Feature Flag Toggles */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Lock className="w-5 h-5 text-amber-400" /> Paid Channels Control</h3>
        <p className="text-gray-400 text-sm mb-6">Master switches for the paid channel system. Toggle these when you're ready to launch paid channels.</p>

        {/* Public Visibility status pill */}
        <div className={`mb-5 rounded-xl border-2 p-4 ${flags?.PAID_CHANNELS_ENABLED ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-gray-700/20 border-gray-600/30'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${flags?.PAID_CHANNELS_ENABLED ? 'bg-emerald-400 animate-pulse' : 'bg-gray-500'}`} />
            <div className="flex-1">
              <p className={`text-sm font-bold ${flags?.PAID_CHANNELS_ENABLED ? 'text-emerald-400' : 'text-gray-400'}`}>
                Public Visibility: {flags?.PAID_CHANNELS_ENABLED ? 'LIVE' : 'HIDDEN'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {flags?.PAID_CHANNELS_ENABLED
                  ? 'All users see "Monetize Channel" + "Subscribe" buttons in chat rooms.'
                  : 'Users see no paid-channel UI. Backend API rejects subscribe/apply attempts.'}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between bg-dark-700/30 rounded-xl p-4 border border-dark-600/30">
            <div>
              <p className="text-white font-medium text-sm">Paid Channels Enabled</p>
              <p className="text-gray-500 text-xs mt-0.5">Master switch — flip ON when users start asking. Until then, the entire feature is invisible.</p>
            </div>
            <button onClick={() => toggleFlag('PAID_CHANNELS_ENABLED')} disabled={toggling === 'PAID_CHANNELS_ENABLED'}
              className={`relative w-14 h-7 rounded-full transition-all ${flags?.PAID_CHANNELS_ENABLED ? 'bg-emerald-500' : 'bg-dark-600'}`}>
              <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${flags?.PAID_CHANNELS_ENABLED ? 'left-8' : 'left-1'}`} />
            </button>
          </div>

          <div className="flex items-center justify-between bg-dark-700/30 rounded-xl p-4 border border-dark-600/30">
            <div>
              <p className="text-white font-medium text-sm">Invite-Only Mode</p>
              <p className="text-gray-500 text-xs mt-0.5">When ON, only users in the allowlist below can apply for paid channels</p>
            </div>
            <button onClick={() => toggleFlag('PAID_CHANNELS_INVITE_ONLY')} disabled={toggling === 'PAID_CHANNELS_INVITE_ONLY'}
              className={`relative w-14 h-7 rounded-full transition-all ${flags?.PAID_CHANNELS_INVITE_ONLY ? 'bg-amber-500' : 'bg-dark-600'}`}>
              <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${flags?.PAID_CHANNELS_INVITE_ONLY ? 'left-8' : 'left-1'}`} />
            </button>
          </div>
        </div>

        <div className={`mt-4 rounded-xl p-3 border ${flags?.PAID_CHANNELS_ENABLED ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-amber-500/5 border-amber-500/15'}`}>
          <p className={`text-xs font-medium ${flags?.PAID_CHANNELS_ENABLED ? 'text-emerald-400' : 'text-amber-400'}`}>
            {flags?.PAID_CHANNELS_ENABLED
              ? (flags?.PAID_CHANNELS_INVITE_ONLY ? 'Paid channels are ON (invite-only). Only allowlisted users can apply.' : 'Paid channels are ON and open to all eligible creators.')
              : 'Paid channels are OFF. Users see "Coming Soon" message.'}
          </p>
        </div>
      </div>

      {/* Treasury Health */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-emerald-400" /> Treasury Health
        </h3>
        <p className="text-gray-500 text-xs mb-4">Live status of your Haythix_Funds wallet on Base. Auto-payouts only fire when payoutReady=true.</p>

        {!treasuryStatus ? (
          <div className="text-gray-500 text-sm">Loading…</div>
        ) : (
          <>
            <div className={`mb-4 rounded-xl border-2 p-3 ${treasuryStatus.payoutReady ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
              <div className="flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full ${treasuryStatus.payoutReady ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
                <p className={`text-sm font-bold ${treasuryStatus.payoutReady ? 'text-emerald-400' : 'text-red-400'}`}>
                  Payouts {treasuryStatus.payoutReady ? 'READY' : 'NOT READY'}
                </p>
              </div>
              {(treasuryStatus.errors || []).length > 0 && (
                <ul className="mt-2 ml-4 list-disc text-[11px] text-red-300 space-y-0.5">
                  {treasuryStatus.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="bg-dark-700/30 rounded-lg p-3">
                <p className="text-gray-500 mb-1">TREASURY_WALLET env</p>
                <p className={`font-mono ${treasuryStatus.treasuryWallet?.configured ? 'text-white' : 'text-red-400'}`}>
                  {treasuryStatus.treasuryWallet?.configured ? `${treasuryStatus.treasuryWallet.value.slice(0, 10)}...${treasuryStatus.treasuryWallet.value.slice(-8)}` : 'NOT SET'}
                </p>
              </div>
              <div className="bg-dark-700/30 rounded-lg p-3">
                <p className="text-gray-500 mb-1">TREASURY_HOT_PK env</p>
                <p className={`${treasuryStatus.treasuryHotPk?.configured ? 'text-emerald-400' : 'text-red-400'} font-bold`}>
                  {treasuryStatus.treasuryHotPk?.configured ? '✓ Configured' : '✗ NOT SET'}
                </p>
              </div>
              <div className="bg-dark-700/30 rounded-lg p-3 col-span-2">
                <p className="text-gray-500 mb-1">Derived from PK</p>
                <p className={`font-mono ${treasuryStatus.addressMatch ? 'text-emerald-400' : 'text-red-400'}`}>
                  {treasuryStatus.derivedAddress ? `${treasuryStatus.derivedAddress.slice(0, 10)}...${treasuryStatus.derivedAddress.slice(-8)}` : '—'}
                </p>
                <p className={`text-[10px] mt-0.5 ${treasuryStatus.addressMatch ? 'text-emerald-400' : 'text-red-400'}`}>
                  {treasuryStatus.addressMatch === true ? '✓ Matches TREASURY_WALLET' : treasuryStatus.addressMatch === false ? '✗ MISMATCH' : '—'}
                </p>
              </div>
            </div>

            {/* Multi-chain balance breakdown */}
            <div className="mt-4">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Per-chain balances (all chains use same wallet address)</p>
              <div className="space-y-2">
                {treasuryStatus.chains && Object.entries(treasuryStatus.chains).map(([key, c]) => {
                  const icons = { base: '🔵', polygon: '🟣', arbitrum: '🔷' };
                  return (
                    <div key={key} className={`rounded-lg p-3 border ${c.payoutReady ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-amber-500/5 border-amber-500/20'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-base">{icons[key] || '⚡'}</span>
                          <p className="text-sm font-bold text-white">{c.label}</p>
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${c.payoutReady ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                            {c.payoutReady ? '✓ READY' : '⚠ LOW GAS'}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <p className="text-gray-500 text-[10px]">ETH balance (gas)</p>
                          <p className="font-mono text-white">{c.ethBalance.toFixed(6)} ETH</p>
                          <p className="text-[10px] text-gray-600">~{c.payoutsLeft} payouts left</p>
                        </div>
                        <div>
                          <p className="text-gray-500 text-[10px]">USDC balance (your 20%)</p>
                          <p className="font-mono text-white">${c.usdcBalance.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {treasuryStatus.totalUsdcAllChains != null && (
                <div className="mt-3 bg-dark-700/40 rounded-lg p-3 flex items-center justify-between">
                  <p className="text-xs text-gray-400">Total USDC across all chains</p>
                  <p className="text-lg font-bold text-emerald-400 font-mono">${treasuryStatus.totalUsdcAllChains.toFixed(2)}</p>
                </div>
              )}
              <p className="text-[10px] text-gray-600 mt-2">Sweep accumulated USDC weekly from each chain to your cold Ledger wallet</p>
            </div>
            <button onClick={loadData} className="mt-3 text-[10px] text-gray-500 hover:text-white">Refresh status</button>
          </>
        )}
      </div>

      {/* Invite Allowlist */}
      {flags?.PAID_CHANNELS_INVITE_ONLY && (
        <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
          <h3 className="text-lg font-bold text-white mb-4">Invite Allowlist ({allowlist.length})</h3>
          <div className="flex gap-2 mb-4">
            <input type="text" value={newAllowlistUser} onChange={e => setNewAllowlistUser(e.target.value)}
              placeholder="User ID to add" className="flex-1 bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none" />
            <button onClick={addToAllowlist} className="px-4 py-2.5 rounded-xl bg-amber-500 text-dark-900 font-bold text-sm">Add</button>
          </div>
          {allowlist.length === 0 ? (
            <p className="text-gray-600 text-sm">No users in allowlist yet. Add user IDs to allow them to apply for paid channels.</p>
          ) : (
            <div className="space-y-1">
              {allowlist.map(uid => (
                <div key={uid} className="flex items-center justify-between bg-dark-700/30 rounded-lg px-3 py-2">
                  <span className="text-white text-xs font-mono">{uid}</span>
                  <button onClick={async () => {
                    try {
                      const r = await managePaidChannelAllowlist('remove', uid);
                      setAllowlist(r.allowlist || []);
                    } catch (err) { onToast({ type: 'error', text: err.message }); }
                  }} className="text-red-400 text-xs hover:text-red-300">Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pending Applications */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <h3 className="text-lg font-bold text-white mb-4">Pending Applications ({pending.length})</h3>
        {pending.length === 0 ? (
          <p className="text-gray-600 text-sm">No pending applications. When creators apply, their applications will appear here for review.</p>
        ) : (
          <div className="space-y-3">
            {pending.map(app => (
              <div key={app.applicationId} className="bg-dark-700/30 rounded-xl p-4 border border-dark-600/30">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-white font-medium text-sm">Channel: {app.channelId}</p>
                    <p className="text-gray-500 text-xs">User: {app.userId} | {app.tierCount} tier(s) | Submitted: {new Date(app.submittedAt).toLocaleDateString()}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => approveApp(app.applicationId)} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-500 text-dark-900">Approve</button>
                    <button onClick={() => rejectApp(app.applicationId)} className="px-3 py-1.5 rounded-lg text-xs font-medium text-red-400 bg-red-500/10 border border-red-500/20">Reject</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Paid Channels Command Center — KPIs + sortable list */}
      <PaidChannelsCommandCenter
        channels={activeChannels.channels || activeChannels}
        kpis={activeChannels.kpis}
        onRefresh={loadData}
        onChannelClick={(channelId) => setDrilldownChannelId(channelId)}
      />
      {drilldownChannelId && (
        <PaidChannelDrilldown
          channelId={drilldownChannelId}
          onClose={() => setDrilldownChannelId(null)}
        />
      )}

      {/* Failed Payouts */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">Failed Creator Payouts <span className="text-xs text-gray-500 font-normal">({failedPayouts.length})</span></h3>
          <button onClick={loadData} className="text-[10px] text-gray-500 hover:text-white">Refresh</button>
        </div>
        {failedPayouts.length === 0 ? (
          <p className="text-gray-600 text-sm">No failed payouts. All creator 80% shares have been sent successfully.</p>
        ) : (
          <>
            <p className="text-[11px] text-amber-400 mb-3">Funds are safe in the treasury. Common causes: invalid creator wallet, treasury ETH low for gas, RPC timeout. Click Retry to attempt again.</p>
            <div className="space-y-2">
              {failedPayouts.map(p => (
                <div key={`${p.subscriptionId}-${p.paymentId}`} className="bg-dark-700/30 rounded-xl p-3 border border-red-500/15">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium">Owed: <span className="text-emerald-400">${p.creatorPayout?.toFixed(2)}</span> USDC <span className="text-gray-500 text-xs">→ {p.creatorPayoutWallet ? `${p.creatorPayoutWallet.slice(0, 8)}...${p.creatorPayoutWallet.slice(-6)}` : 'NO WALLET SET'}</span></p>
                      <p className="text-[11px] text-gray-500 mt-1">Channel: <code className="text-gray-400">{p.channelId}</code></p>
                      <p className="text-[11px] text-red-400 mt-1">Reason: <span className="text-red-300 font-mono">{p.payoutReason}</span> · Retries: {p.payoutRetryCount}/5</p>
                      {p.payoutError && <p className="text-[10px] text-gray-600 mt-0.5 truncate" title={p.payoutError}>{p.payoutError}</p>}
                    </div>
                    <button onClick={() => retryPayout(p.subscriptionId, p.paymentId)}
                      disabled={retrying === `${p.subscriptionId}:${p.paymentId}` || !p.creatorPayoutWallet}
                      className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-500/20 border border-amber-500/40 text-amber-400 disabled:opacity-40">
                      {retrying === `${p.subscriptionId}:${p.paymentId}` ? 'Retrying…' : 'Retry'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Tab: Business (CEO / CTO operating view) ────────────────────────────────

function KpiCard({ label, value, accent = 'text-white', sub, icon: Icon, iconBg = 'bg-accent-primary/10', iconColor = 'text-accent-primary' }) {
  return (
    <div className="bg-dark-800 rounded-2xl p-5 border border-dark-700/50">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className={`w-10 h-10 rounded-xl ${iconBg} ${iconColor} flex items-center justify-center flex-shrink-0`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-gray-500 text-[10px] uppercase tracking-wider">{label}</p>
          <p className={`text-2xl font-bold mt-0.5 truncate ${accent}`}>{value}</p>
          {sub && <p className="text-gray-600 text-[11px] mt-0.5 truncate">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

function ChainBalanceRow({ chain }) {
  const ok = chain.configured && !chain.error;
  const lowGas = ok && chain.nativeBalance != null && chain.nativeBalance < 0.01;
  const lowUsdc = ok && chain.usdcBalance != null && chain.usdcBalance < 10;
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-dark-700/30 border border-dark-600/30">
      <div className={`w-2 h-2 rounded-full ${ok ? 'bg-emerald-400' : 'bg-gray-500'}`}
        style={ok ? { boxShadow: '0 0 6px #34d399' } : undefined} />
      <p className="text-sm font-medium text-white flex-1 flex items-center gap-2">
        {chain.name}
        {chain.usingFallback && (
          <span className="text-[9px] uppercase tracking-wider text-amber-400/70 font-normal" title="Using public RPC fallback — set your own paid RPC URL for production reliability">public RPC</span>
        )}
      </p>
      {!chain.configured ? (
        <span className="text-[10px] text-gray-500">{chain.reason || 'Not configured'}</span>
      ) : chain.error ? (
        <span className="text-[10px] text-red-400 truncate max-w-[200px]" title={chain.error}>RPC error</span>
      ) : (
        <div className="flex items-center gap-3">
          <span className={`text-xs font-mono ${lowUsdc ? 'text-amber-400' : 'text-gray-300'}`}>
            {chain.usdcBalance.toFixed(2)} USDC
          </span>
          <span className={`text-xs font-mono ${lowGas ? 'text-amber-400' : 'text-gray-500'}`}>
            {chain.nativeBalance.toFixed(4)} {chain.native}
          </span>
        </div>
      )}
    </div>
  );
}

function WorkerStatusRow({ worker }) {
  const dt = worker.lastRunAt ? new Date(worker.lastRunAt) : null;
  const outcomeColor = worker.lastOutcome === 'success' ? 'text-emerald-400'
    : worker.lastOutcome === 'failure' ? 'text-red-400' : 'text-gray-500';
  const dotColor = worker.lastOutcome === 'success' ? 'bg-emerald-400'
    : worker.lastOutcome === 'failure' ? 'bg-red-400' : 'bg-gray-500';
  const ageMs = dt ? Date.now() - dt.getTime() : null;
  const ageLabel = ageMs == null ? '—'
    : ageMs < 60_000 ? `${Math.floor(ageMs / 1000)}s ago`
    : ageMs < 3_600_000 ? `${Math.floor(ageMs / 60_000)}m ago`
    : `${Math.floor(ageMs / 3_600_000)}h ago`;
  return (
    <div className="flex items-center gap-3 p-2.5 rounded-lg bg-dark-700/30 border border-dark-600/30" title={worker.lastError || ''}>
      <span className={`w-2 h-2 rounded-full ${dotColor} flex-shrink-0`}
        style={worker.lastOutcome === 'success' ? { boxShadow: '0 0 6px #34d399' } : undefined} />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-white truncate">{worker.label}</p>
        <p className="text-[10px] text-gray-500">
          {worker.schedule}
          {worker.durationMs != null && <> · {worker.durationMs}ms</>}
        </p>
      </div>
      <div className="text-right">
        <p className={`text-[10px] font-bold uppercase ${outcomeColor}`}>{worker.lastOutcome}</p>
        <p className="text-[10px] text-gray-600">{ageLabel}</p>
      </div>
    </div>
  );
}

function AuditEntryRow({ entry }) {
  const dt = entry.timestamp ? new Date(entry.timestamp) : null;
  const ok = entry.outcome === 'success';
  return (
    <div className="flex items-start gap-3 p-2.5 rounded-lg bg-dark-700/30 border border-dark-600/30">
      <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${ok ? 'bg-emerald-400' : 'bg-red-400'}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-medium text-white truncate">{entry.action}</p>
          <span className={`text-[10px] font-bold uppercase ${ok ? 'text-emerald-400' : 'text-red-400'}`}>{entry.outcome}</span>
        </div>
        <p className="text-[10px] text-gray-500 mt-0.5">
          {dt ? dt.toLocaleString() : entry.timestamp} · {entry.actorEmail || entry.actorUserId || 'unknown'}
        </p>
      </div>
    </div>
  );
}

function BusinessTab({ onToast }) {
  const { user } = useAuth();
  // Owner sees everything; otherwise check the explicit financial-read perm.
  // Owner detection mirrors AdminPage / AdminRoute logic.
  const isOwner = user?.role === 'admin' || (Array.isArray(user?.roles) && user.roles.includes('owner'));
  const canSeeFinancial = isOwner
    || (Array.isArray(user?.permissions) && user.permissions.includes('business.read_financial'));

  const [kpis, setKpis] = useState(null);
  const [treasury, setTreasury] = useState(null);
  const [workers, setWorkers] = useState(null);
  const [audit, setAudit] = useState([]);
  const [referrals, setReferrals] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [k, t, w, a, r] = await Promise.all([
        getBusinessKpis().catch(e => { onToast({ type: 'error', text: `KPIs: ${e.message}` }); return null; }),
        getTreasuryBalances().catch(e => { onToast({ type: 'error', text: `Treasury: ${e.message}` }); return null; }),
        getWorkerStatus().catch(() => null),
        getMgmtAuditLog().catch(() => ({ entries: [] })),
        getReferralsDashboard().catch(() => null),
      ]);
      setKpis(k);
      setTreasury(t);
      setWorkers(w);
      setAudit(a?.entries || []);
      setReferrals(r);
    } finally { setLoading(false); }
  }, [onToast]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex justify-center py-16"><Spinner className="text-accent-primary" /></div>;
  }

  const founderPct = kpis?.founderSlots
    ? Math.round((kpis.founderSlots.used / kpis.founderSlots.total) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header note + refresh */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-500/5 border border-amber-500/15 rounded-2xl">
        <div className="flex items-center gap-3">
          <DollarSign className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-gray-400 text-xs leading-relaxed">
            <span className="text-amber-400 font-semibold">Business view — </span>
            {canSeeFinancial
              ? 'Revenue, growth, treasury, and worker heartbeat. Refresh manually — these don’t auto-poll.'
              : 'Growth, treasury, and worker heartbeat. Revenue cards are hidden by your role; ask the Owner if you need them.'}
          </p>
        </div>
        <button onClick={load} className="btn-ghost p-1.5 flex items-center gap-1 text-xs">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Headline KPIs — financial cards (MRR, ARR, Trial→paid) only render
          for users with business.read_financial. Total users is operational
          and shown to everyone. */}
      {kpis && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {canSeeFinancial && (
              <>
                <KpiCard label="MRR" icon={DollarSign} iconBg="bg-emerald-500/10" iconColor="text-emerald-400"
                  accent="text-emerald-400" value={`$${kpis.mrr.toFixed(2)}`}
                  sub={`${kpis.payingUsers} paying users`} />
                <KpiCard label="ARR projection" icon={TrendingUp} iconBg="bg-emerald-500/10" iconColor="text-emerald-400"
                  accent="text-emerald-400" value={`$${kpis.arr.toFixed(0)}`}
                  sub="MRR × 12 (snapshot, not forecast)" />
              </>
            )}
            <KpiCard label="Total users" icon={Users} iconBg="bg-purple-500/10" iconColor="text-purple-400"
              accent="text-purple-400" value={kpis.totalUsers}
              sub={canSeeFinancial ? `${kpis.complimentaryCount} complimentary` : 'registered accounts'} />
            {canSeeFinancial && (
              <KpiCard label="Trial → paid" icon={TrendingUp} iconBg="bg-violet-500/10" iconColor="text-violet-400"
                accent="text-violet-400"
                value={kpis.trialToPaid.pct != null ? `${kpis.trialToPaid.pct}%` : '—'}
                sub={`${kpis.trialToPaid.converted} of ${kpis.trialToPaid.eligibleUsers} eligible`} />
            )}
          </div>

          {/* Signups + verification — operational, shown to everyone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SectionCard icon={Users} title="Signups" subtitle="New accounts in the last…">
              <div className="grid grid-cols-3 gap-3">
                <KpiCard label="24h"    value={kpis.signups.today} accent="text-cyan-400" />
                <KpiCard label="7d"     value={kpis.signups.week}  accent="text-cyan-400" />
                <KpiCard label="30d"    value={kpis.signups.month} accent="text-cyan-400" />
              </div>
            </SectionCard>
            <SectionCard icon={Check} title="Email verification" subtitle="Verified vs total accounts">
              <div className="space-y-2">
                <div className="flex items-baseline justify-between">
                  <span className="text-3xl font-bold text-emerald-400">{kpis.emailVerification.pct != null ? `${kpis.emailVerification.pct}%` : '—'}</span>
                  <span className="text-xs text-gray-500">{kpis.emailVerification.verified} / {kpis.emailVerification.total}</span>
                </div>
                <div className="h-1.5 bg-dark-700 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                    style={{ width: `${kpis.emailVerification.pct || 0}%` }} />
                </div>
              </div>
            </SectionCard>
          </div>

          {/* Financial-only sections: tier mix, founder programme, subscription status */}
          {canSeeFinancial && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SectionCard icon={Crown} title="Tier breakdown" subtitle="Effective tier (max of paid + complimentary)">
                  <div className="space-y-2">
                    {Object.entries(kpis.tiers).map(([tier, count]) => (
                      <div key={tier} className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 w-16 capitalize">{tier}</span>
                        <div className="flex-1 h-2 bg-dark-700 rounded-full overflow-hidden">
                          <div className="h-full bg-accent-primary rounded-full transition-all duration-500"
                            style={{ width: `${kpis.totalUsers > 0 ? (count / kpis.totalUsers) * 100 : 0}%` }} />
                        </div>
                        <span className="text-xs text-white font-medium w-8 text-right">{count}</span>
                      </div>
                    ))}
                  </div>
                </SectionCard>
                <SectionCard icon={Crown} title="Founder programme" subtitle="100 grandfather slots — 50% off forever">
                  <div className="space-y-3">
                    <div className="flex items-baseline justify-between">
                      <span className="text-3xl font-bold text-amber-400">{kpis.founderSlots.used}</span>
                      <span className="text-xs text-gray-500">/ {kpis.founderSlots.total} used · {kpis.founderSlots.remaining} left</span>
                    </div>
                    <div className="h-2 bg-dark-700 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full transition-all duration-500"
                        style={{ width: `${founderPct}%` }} />
                    </div>
                    <p className="text-[11px] text-gray-500 leading-relaxed">
                      Tracks the founders/list.json roster. Filled on first paid checkout that applied the FOUNDER_50_FOREVER coupon.
                    </p>
                  </div>
                </SectionCard>
              </div>

              <SectionCard icon={CreditCard} title="Subscription status" subtitle="What state every user's billing record is in"
                danger={false}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(kpis.subscriptionStatuses).map(([status, count]) => (
                    <div key={status} className="bg-dark-700/40 rounded-xl p-3 border border-dark-600/30">
                      <p className="text-[10px] uppercase tracking-wider text-gray-500">{status.replace(/_/g, ' ')}</p>
                      <p className="text-xl font-bold text-white mt-0.5">{count}</p>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </>
          )}
        </>
      )}

      {/* Treasury balances */}
      {treasury && (
        <SectionCard icon={Wifi} title="Treasury balances"
          subtitle={treasury.configured
            ? `Hot wallet ${treasury.masked} · cached ${treasury.cacheAge != null ? `${treasury.cacheAge}s ago` : 'just now'}`
            : 'TREASURY_HOT_PK not configured — no balances to show'}>
          {!treasury.configured ? (
            <div className="text-xs text-gray-500 px-1 py-2">{treasury.error || 'Set TREASURY_HOT_PK to enable balance polling.'}</div>
          ) : (
            <div className="space-y-2">
              {treasury.chains.map(c => <ChainBalanceRow key={c.id} chain={c} />)}
              <p className="text-[10px] text-gray-600 leading-relaxed mt-3">
                Amber values flag wallets below the safety thresholds (USDC &lt;10 or native &lt;0.01).
                Top up before the auto-renew sweep runs out of gas.
              </p>
            </div>
          )}
        </SectionCard>
      )}

      {/* Referral programme dashboard — financial, hidden for non-finance admins */}
      {referrals && canSeeFinancial && (
        <SectionCard icon={TrendingUp} title="Referral programme"
          subtitle={referrals.terms?.summary || 'Referral metrics across the platform'}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <KpiCard label="Paid lifetime" icon={DollarSign} iconBg="bg-emerald-500/10" iconColor="text-emerald-400"
              accent="text-emerald-400" value={`$${(referrals.kpis?.totalPaidLifetime || 0).toFixed(2)}`}
              sub={`across ${referrals.kpis?.totalReferrers || 0} referrers`} />
            <KpiCard label="Monthly accrual" icon={TrendingUp} iconBg="bg-emerald-500/10" iconColor="text-emerald-400"
              accent="text-emerald-400" value={`$${(referrals.kpis?.monthlyAccrual || 0).toFixed(2)}`}
              sub={`${referrals.kpis?.stillAccruing || 0} active relationships`} />
            <KpiCard label="Referees signed up" icon={Users} iconBg="bg-purple-500/10" iconColor="text-purple-400"
              accent="text-purple-400" value={referrals.kpis?.totalReferees || 0}
              sub={`${referrals.kpis?.payingReferees || 0} paying`} />
            <KpiCard label="Trial → paid" icon={TrendingUp} iconBg="bg-violet-500/10" iconColor="text-violet-400"
              accent="text-violet-400"
              value={referrals.kpis?.conversionPct != null ? `${referrals.kpis.conversionPct}%` : '—'}
              sub={`Avg $${referrals.kpis?.avgPerReferee || 0} per referee`} />
          </div>

          <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-2">Top 10 referrers</p>
          {referrals.topReferrers?.length === 0 ? (
            <p className="text-xs text-gray-600 text-center py-4">No referrals yet.</p>
          ) : (
            <div className="bg-dark-700/30 rounded-xl border border-dark-600/30 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-dark-700/50">
                  <tr className="text-gray-500 uppercase text-[10px] tracking-wider">
                    <th className="text-left px-4 py-2">#</th>
                    <th className="text-left px-4 py-2">Referrer</th>
                    <th className="text-left px-4 py-2">Tier</th>
                    <th className="text-right px-4 py-2">Referees</th>
                    <th className="text-right px-4 py-2">Active</th>
                    <th className="text-right px-4 py-2">Monthly</th>
                    <th className="text-right px-4 py-2">Lifetime</th>
                  </tr>
                </thead>
                <tbody>
                  {(referrals.topReferrers || []).map((r, i) => (
                    <tr key={r.userId} className="border-t border-dark-700/30 hover:bg-dark-700/20">
                      <td className="px-4 py-2 text-gray-500">{i + 1}</td>
                      <td className="px-4 py-2 text-white">
                        <p className="font-medium truncate">{r.name}</p>
                        <p className="text-[10px] text-gray-500">{r.email}</p>
                      </td>
                      <td className="px-4 py-2 text-gray-400 capitalize">{r.tier || '—'}</td>
                      <td className="px-4 py-2 text-right text-gray-300">{r.refereeCount}</td>
                      <td className="px-4 py-2 text-right text-emerald-400">{r.activeRefereeCount}</td>
                      <td className="px-4 py-2 text-right text-emerald-400 font-mono">${r.currentMonthlyAccrual.toFixed(2)}</td>
                      <td className="px-4 py-2 text-right text-white font-mono font-semibold">${r.totalEarnedLifetime.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[10px] text-gray-600 mt-3 leading-relaxed">
            <span className="text-gray-500">Definition:</span> <span className="text-emerald-400">Paid lifetime</span> is the authoritative sum of <code className="text-gray-400">user.referralEarnings</code> (incremented by the Stripe webhook on each invoice).
            <span className="text-emerald-400"> Monthly accrual</span> estimates what active referrers are currently earning per month based on referee subscription state — it's a forward-looking projection, not paid revenue.
          </p>
        </SectionCard>
      )}

      {/* Worker heartbeat */}
      {workers && (
        <SectionCard icon={Activity} title="Worker heartbeat"
          subtitle={`${workers.reportingCount ?? 0} of ${workers.totalWorkers ?? workers.workers.length} workers reporting · ${workers.note || ''}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {workers.workers.map(w => <WorkerStatusRow key={w.id} worker={w} />)}
          </div>
        </SectionCard>
      )}

      {/* Admin audit log */}
      <SectionCard icon={ScrollText} title="Admin audit log"
        subtitle={`Last ${audit.length} admin actions across all mgmt ops`}>
        {audit.length === 0 ? (
          <p className="text-xs text-gray-600 text-center py-4">No admin actions recorded yet.</p>
        ) : (
          <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
            {audit.map(entry => <AuditEntryRow key={entry.id} entry={entry} />)}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// ─── Tab: Team Administration (Owner-only) ───────────────────────────────────

function TeamMemberCard({ member, roles, onChangeRoles, onRevoke, busy }) {
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState(member.roles);

  if (member.isOwner) {
    return (
      <div className="bg-dark-700/40 rounded-xl p-4 border border-amber-500/30">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-white truncate">{member.name || member.email}</p>
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">OWNER</span>
            </div>
            <p className="text-[11px] text-gray-500 truncate">{member.email}</p>
            <p className="text-[10px] text-gray-600 mt-1">Full platform access. Owner role cannot be edited from this UI.</p>
          </div>
          <Crown className="w-4 h-4 text-amber-400 flex-shrink-0" />
        </div>
      </div>
    );
  }

  const toggleRole = (rid) => {
    setSelected(s => s.includes(rid) ? s.filter(x => x !== rid) : [...s, rid]);
  };

  return (
    <div className="bg-dark-700/40 rounded-xl p-4 border border-dark-600/30">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white truncate">{member.name || member.email}</p>
          <p className="text-[11px] text-gray-500 truncate">{member.email}</p>
          <div className="flex items-center gap-2 mt-1.5">
            {!member.mfaEnabled && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">MFA OFF</span>
            )}
            {!member.emailVerified && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500/15 text-red-400">UNVERIFIED</span>
            )}
          </div>
        </div>
        {!editing ? (
          <div className="flex items-center gap-1">
            <button onClick={() => setEditing(true)} className="btn-ghost text-[11px]">Edit roles</button>
            <button onClick={() => onRevoke(member)} disabled={busy} className="text-red-400 hover:text-red-300 text-[11px] px-2 py-1 rounded hover:bg-red-500/10">Revoke</button>
          </div>
        ) : null}
      </div>

      {!editing ? (
        <div className="flex flex-wrap gap-1.5">
          {member.roles.length === 0 ? (
            <span className="text-[11px] text-gray-500 italic">No roles assigned</span>
          ) : (
            member.roles.map(rid => {
              const def = roles.find(r => r.id === rid);
              return (
                <span key={rid} className="text-[10px] font-bold px-2 py-0.5 rounded bg-accent-primary/15 text-accent-primary border border-accent-primary/30">
                  {def?.label || rid}
                </span>
              );
            })
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-gray-500">Roles</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {roles.map(r => (
              <label key={r.id} className="flex items-start gap-2 p-2 rounded-lg bg-dark-800/60 hover:bg-dark-800 cursor-pointer">
                <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleRole(r.id)}
                  className="mt-1 accent-amber-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-white">{r.label}</p>
                  <p className="text-[10px] text-gray-500 leading-snug">{r.description}</p>
                </div>
              </label>
            ))}
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={() => { setEditing(false); setSelected(member.roles); }} className="btn-ghost text-[11px]">Cancel</button>
            <button onClick={async () => { await onChangeRoles(member, selected); setEditing(false); }}
              disabled={busy} className="btn-primary text-[11px] flex items-center gap-1.5">
              <Save className="w-3 h-3" /> Save roles
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function InviteForm({ roles, onInvite, busy }) {
  const [email, setEmail] = useState('');
  const [selected, setSelected] = useState([]);

  const toggleRole = (rid) => {
    setSelected(s => s.includes(rid) ? s.filter(x => x !== rid) : [...s, rid]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || selected.length === 0) return;
    const ok = await onInvite(email.trim().toLowerCase(), selected);
    if (ok) { setEmail(''); setSelected([]); }
  };

  return (
    <form onSubmit={submit} className="bg-dark-700/40 rounded-xl p-4 border border-dark-600/30 space-y-3">
      <div>
        <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Invite a new admin</p>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder="teammate@yourcompany.com"
          className="w-full bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-accent-primary focus:outline-none" />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Roles</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
          {roles.map(r => (
            <label key={r.id} className="flex items-start gap-2 p-2 rounded-lg bg-dark-800/60 hover:bg-dark-800 cursor-pointer">
              <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleRole(r.id)}
                className="mt-1 accent-amber-500" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-white">{r.label}</p>
                <p className="text-[10px] text-gray-500 leading-snug">{r.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>
      <button type="submit" disabled={busy || !email.trim() || selected.length === 0}
        className="btn-primary text-xs flex items-center gap-1.5">
        {busy ? <><Spinner size="sm" /> Sending invite…</> : <><Users className="w-3 h-3" /> Send invite</>}
      </button>
    </form>
  );
}

function PendingInviteRow({ invite, onRevoke, onCopyUrl }) {
  const expired = invite.expired;
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${expired ? 'bg-red-500/5 border-red-500/20' : 'bg-dark-700/30 border-dark-600/30'}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">{invite.email}</p>
        <p className="text-[10px] text-gray-500 truncate">
          {invite.roles.join(' · ')} · invited by {invite.invitedByEmail || 'unknown'} · {new Date(invite.createdAt).toLocaleString()}
        </p>
      </div>
      {expired ? (
        <span className="text-[10px] font-bold uppercase text-red-400">expired</span>
      ) : (
        <span className="text-[10px] text-gray-500">expires {new Date(invite.expiresAt).toLocaleDateString()}</span>
      )}
      {invite.inviteUrl && (
        <button onClick={() => onCopyUrl(invite.inviteUrl)} title="Copy invite link" className="btn-ghost p-1.5">
          <Copy className="w-3 h-3" />
        </button>
      )}
      <button onClick={() => onRevoke(invite)} className="text-red-400 hover:text-red-300 text-[10px] px-2 py-1">Revoke</button>
    </div>
  );
}

function TeamAdminTab({ onToast }) {
  const { runWithStepUp } = useStepUpGate();
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, r, i] = await Promise.all([
        getAdminTeamMembers().catch(e => { onToast({ type: 'error', text: `Members: ${e.message}` }); return { members: [] }; }),
        getAdminTeamRoles().catch(e => { onToast({ type: 'error', text: `Roles: ${e.message}` }); return { roles: [] }; }),
        getAdminTeamInvites().catch(() => ({ invites: [] })),
      ]);
      setMembers(m.members || []);
      setRoles(r.roles || []);
      setInvites(i.invites || []);
    } finally { setLoading(false); }
  }, [onToast]);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async (email, selectedRoles) => {
    setBusy(true);
    try {
      const result = await runWithStepUp(
        () => createAdminTeamInvite(email, selectedRoles),
        'Invite new admin'
      );
      onToast({ type: 'success', text: result.emailSent
        ? `Invite sent to ${email}`
        : `Invite created — email failed; copy link from list below` });
      load();
      return true;
    } catch (err) { onToast({ type: 'error', text: err.message }); return false; }
    finally { setBusy(false); }
  };

  const handleRoleChange = async (member, newRoles) => {
    setBusy(true);
    try {
      await runWithStepUp(
        () => updateAdminTeamMemberRoles(member.userId, newRoles),
        `Update roles for ${member.email}`
      );
      onToast({ type: 'success', text: `${member.email} roles updated` });
      load();
    } catch (err) {
      if (err.code === 'APPROVAL_REQUIRED') {
        onToast({ type: 'success', text: `Approval requested — see Approvals tab to track #${err.approvalId?.slice(0, 8)}` });
      } else { onToast({ type: 'error', text: err.message }); }
    }
    finally { setBusy(false); }
  };

  const handleRevoke = async (member) => {
    if (!window.confirm(`Revoke ALL admin roles from ${member.email}? They will lose access on next login (or sooner if their session is force-revoked via the Sessions tab).`)) return;
    setBusy(true);
    try {
      await runWithStepUp(
        () => revokeAdminTeamMember(member.userId),
        `Revoke admin access for ${member.email}`
      );
      onToast({ type: 'success', text: `Admin access revoked from ${member.email}` });
      load();
    } catch (err) {
      if (err.code === 'APPROVAL_REQUIRED') {
        onToast({ type: 'success', text: `Approval requested — see Approvals tab to track #${err.approvalId?.slice(0, 8)}` });
      } else { onToast({ type: 'error', text: err.message }); }
    }
    finally { setBusy(false); }
  };

  const handleRevokeInvite = async (invite) => {
    if (!window.confirm(`Revoke pending invite for ${invite.email}?`)) return;
    try {
      await revokeAdminTeamInvite(invite.id);
      onToast({ type: 'success', text: 'Invite revoked' });
      load();
    } catch (err) { onToast({ type: 'error', text: err.message }); }
  };

  const copyUrl = (url) => {
    navigator.clipboard?.writeText(url);
    onToast({ type: 'success', text: 'Invite link copied to clipboard' });
  };

  if (loading) return <div className="flex justify-center py-16"><Spinner className="text-accent-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-500/5 border border-amber-500/15 rounded-2xl">
        <div className="flex items-center gap-3">
          <Crown className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-gray-400 text-xs leading-relaxed">
            <span className="text-amber-400 font-semibold">Owner-only — </span>
            Invite teammates, assign roles, revoke access. Owner role itself cannot be granted from this UI.
          </p>
        </div>
        <button onClick={load} className="btn-ghost p-1.5 flex items-center gap-1 text-xs">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard icon={Users} title="Invite admin" subtitle="Email a 7-day link to onboard a new team member with the roles you pick">
          <InviteForm roles={roles} onInvite={handleInvite} busy={busy} />
        </SectionCard>
        <SectionCard icon={Clock} title={`Pending invites · ${invites.length}`} subtitle="Outstanding admin invitations that haven't been accepted yet">
          {invites.length === 0 ? (
            <p className="text-xs text-gray-600 text-center py-4">No pending invites.</p>
          ) : (
            <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
              {invites.map(inv => <PendingInviteRow key={inv.id} invite={inv} onRevoke={handleRevokeInvite} onCopyUrl={copyUrl} />)}
            </div>
          )}
        </SectionCard>
      </div>

      <SectionCard icon={Shield} title={`Admin team · ${members.length}`} subtitle="Edit roles or revoke access. Owner cannot be edited from this UI.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {members.map(m => (
            <TeamMemberCard key={m.userId} member={m} roles={roles}
              onChangeRoles={handleRoleChange} onRevoke={handleRevoke} busy={busy} />
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

// ─── Tab: Audit Log v2 (filter + CSV) ────────────────────────────────────────

function AuditLogTab() {
  const [data, setData] = useState({ entries: [], distinctActions: [], distinctActors: [], totalScanned: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ actorEmail: '', action: '', outcome: '', from: '', to: '', search: '' });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        actorEmail: filters.actorEmail || undefined,
        action: filters.action || undefined,
        outcome: filters.outcome || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined,
        search: filters.search || undefined,
        limit: 500,
      };
      const d = await getFilteredAuditLog(params);
      setData(d);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const csvUrl = (() => {
    const token = localStorage.getItem('authToken');
    const params = {
      actorEmail: filters.actorEmail || undefined,
      action: filters.action || undefined,
      outcome: filters.outcome || undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
      search: filters.search || undefined,
    };
    // The CSV endpoint reads auth from the Bearer header, so we have to
    // download via fetch + blob (window.open can't set auth headers).
    return { params, token };
  })();

  const downloadCsv = async () => {
    try {
      const url = getAuditLogCsvUrl(csvUrl.params);
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${csvUrl.token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `haythix-audit-log-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) {
      setError(`CSV download failed: ${e.message}`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-500/5 border border-amber-500/15 rounded-2xl">
        <div className="flex items-center gap-3">
          <ScrollText className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-gray-400 text-xs leading-relaxed">
            <span className="text-amber-400 font-semibold">Audit log — </span>
            Every admin action captured with actor, role-at-time, IP, and outcome. {data.totalScanned} entries scanned.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={downloadCsv} className="btn-ghost text-xs flex items-center gap-1.5">
            <Download className="w-3 h-3" /> Export CSV
          </button>
          <button onClick={load} className="btn-ghost p-1.5"><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
        <input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          placeholder="Search action / details / user…"
          className="md:col-span-2 input bg-dark-700 text-xs" />
        <select value={filters.actorEmail} onChange={e => setFilters(f => ({ ...f, actorEmail: e.target.value }))}
          className="input bg-dark-700 text-xs">
          <option value="">All actors</option>
          {data.distinctActors.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}
          className="input bg-dark-700 text-xs">
          <option value="">All actions</option>
          {data.distinctActions.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={filters.outcome} onChange={e => setFilters(f => ({ ...f, outcome: e.target.value }))}
          className="input bg-dark-700 text-xs">
          <option value="">All outcomes</option>
          <option value="success">success</option>
          <option value="failure">failure</option>
        </select>
        <div className="grid grid-cols-2 gap-1">
          <input type="date" value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
            className="input bg-dark-700 text-xs" />
          <input type="date" value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
            className="input bg-dark-700 text-xs" />
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-300">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Spinner className="text-accent-primary" /></div>
      ) : data.entries.length === 0 ? (
        <p className="text-center text-sm text-gray-500 py-12">No entries match your filters.</p>
      ) : (
        <div className="bg-dark-800 rounded-2xl border border-dark-700/50 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-dark-700/50">
              <tr className="text-gray-500 uppercase text-[10px] tracking-wider">
                <th className="text-left px-4 py-2">Time</th>
                <th className="text-left px-4 py-2">Actor</th>
                <th className="text-left px-4 py-2">Roles</th>
                <th className="text-left px-4 py-2">IP</th>
                <th className="text-left px-4 py-2">Action</th>
                <th className="text-left px-4 py-2">Outcome</th>
                <th className="text-left px-4 py-2">Details</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map(e => (
                <tr key={e.id} className="border-t border-dark-700/30 hover:bg-dark-700/20">
                  <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{new Date(e.timestamp).toLocaleString()}</td>
                  <td className="px-4 py-2 text-white">{e.actorEmail || '—'}</td>
                  <td className="px-4 py-2 text-gray-400">{(e.actorRoles || []).join(', ') || '—'}</td>
                  <td className="px-4 py-2 text-gray-500 font-mono text-[10px]">{e.actorIp || '—'}</td>
                  <td className="px-4 py-2 text-amber-400 font-medium">{e.action}</td>
                  <td className="px-4 py-2"><span className={e.outcome === 'success' ? 'text-emerald-400' : 'text-red-400'}>{e.outcome}</span></td>
                  <td className="px-4 py-2 text-gray-500 font-mono text-[10px] max-w-md truncate" title={typeof e.details === 'string' ? e.details : JSON.stringify(e.details)}>
                    {typeof e.details === 'string' ? e.details : JSON.stringify(e.details || {})}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Admin Sessions (Owner-only, force-logout) ──────────────────────────

// Relative-time formatter: "2m ago", "3h ago", "5d ago". For sessions inactive
// >30d, falls back to absolute date so the operator doesn't have to do math.
function formatAge(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'now';
  if (ms < 60_000)        return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000)     return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000)    return `${Math.floor(ms / 3_600_000)}h ago`;
  if (ms < 30 * 86_400_000) return `${Math.floor(ms / 86_400_000)}d ago`;
  return new Date(iso).toLocaleDateString();
}

const STALE_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;

function SessionRow({ session, admin, isThisDevice, onRevoke }) {
  const ageMs = session.lastSeenAt ? Date.now() - new Date(session.lastSeenAt).getTime() : null;
  const isStale = ageMs != null && ageMs > STALE_THRESHOLD_MS;
  return (
    <div className={`flex items-center gap-3 p-2.5 rounded-lg border ${isStale ? 'bg-amber-500/5 border-amber-500/20' : 'bg-dark-700/30 border-dark-600/30'}`}>
      <Activity className={`w-3 h-3 flex-shrink-0 ${isThisDevice ? 'text-cyan-400' : isStale ? 'text-amber-400' : 'text-emerald-400'}`}
        style={isThisDevice || (!isStale && !isThisDevice) ? { filter: 'drop-shadow(0 0 4px currentColor)' } : undefined} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-xs text-white truncate">{session.userAgent}</p>
          {isThisDevice && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">THIS DEVICE</span>
          )}
          {isStale && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">STALE</span>
          )}
        </div>
        <p className="text-[10px] text-gray-500">
          {admin && <span className="text-gray-400">{admin.email} · </span>}
          IP {session.lastSeenIp || '—'} · last seen {formatAge(session.lastSeenAt)}
        </p>
      </div>
      {onRevoke && !isThisDevice && (
        <button onClick={onRevoke} className="text-red-400 hover:text-red-300 text-[10px] px-2 py-1">Revoke</button>
      )}
    </div>
  );
}

function AdminSessionsTab({ onToast }) {
  const { runWithStepUp } = useStepUpGate();
  const [data, setData] = useState({ admins: [], totalActiveSessions: 0 });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('grouped'); // 'grouped' | 'flat'
  const [search, setSearch] = useState('');
  const [recentRevokes, setRecentRevokes] = useState([]);
  const currentJti = getCurrentSessionJti();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        getAdminSessions(),
        getFilteredAuditLog({ action: 'admin.revoke_session', limit: 5 }).catch(() => ({ entries: [] })),
      ]);
      setData(s);
      setRecentRevokes(a?.entries || []);
    }
    catch (e) { onToast({ type: 'error', text: e.message }); }
    finally { setLoading(false); }
  }, [onToast]);

  useEffect(() => { load(); }, [load]);

  const revokeOne = async (admin, jti) => {
    if (jti === currentJti) {
      onToast({ type: 'error', text: "That's your current session — sign out via the user menu instead." });
      return;
    }
    if (!window.confirm(`Force-logout this session for ${admin.email}? They'll need to sign in again on that device.`)) return;
    try {
      await runWithStepUp(
        () => revokeAdminSession(admin.userId, { jti }),
        `Force-logout session for ${admin.email}`
      );
      onToast({ type: 'success', text: 'Session revoked' });
      load();
    } catch (e) { onToast({ type: 'error', text: e.message }); }
  };

  const revokeAll = async (admin) => {
    if (!window.confirm(`Force-logout ALL ${admin.activeSessionCount} sessions for ${admin.email}? They'll need to sign in again everywhere.`)) return;
    try {
      await runWithStepUp(
        () => revokeAdminSession(admin.userId, { allSessionsForUser: true, allOther: true }),
        `Force-logout all sessions for ${admin.email}`
      );
      onToast({ type: 'success', text: 'All sessions revoked' });
      load();
    } catch (e) { onToast({ type: 'error', text: e.message }); }
  };

  // Build search-filtered + flat list view
  const filteredAdmins = (data.admins || []).filter(a => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (a.email || '').toLowerCase().includes(s) ||
           (a.name || '').toLowerCase().includes(s) ||
           (a.roles || []).some(r => r.toLowerCase().includes(s));
  });
  const flatSessions = filteredAdmins.flatMap(a => a.sessions.map(s => ({ ...s, admin: a })))
    .sort((a, b) => new Date(b.lastSeenAt || 0) - new Date(a.lastSeenAt || 0));
  const staleCount = flatSessions.filter(s => s.lastSeenAt && (Date.now() - new Date(s.lastSeenAt).getTime() > STALE_THRESHOLD_MS)).length;

  if (loading) return <div className="flex justify-center py-16"><Spinner className="text-accent-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-500/5 border border-amber-500/15 rounded-2xl">
        <div className="flex items-center gap-3 min-w-0">
          <Lock className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-gray-400 text-xs leading-relaxed truncate">
            <span className="text-amber-400 font-semibold">Active admin sessions — </span>
            {data.totalActiveSessions} session{data.totalActiveSessions === 1 ? '' : 's'} across {data.admins.length} admin{data.admins.length === 1 ? '' : 's'}
            {staleCount > 0 && <span className="text-amber-400"> · {staleCount} stale</span>}. Force-logout takes effect on the next request.
          </p>
        </div>
        <button onClick={load} className="btn-ghost p-1.5"><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>

      {/* Controls — view toggle + search */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-dark-800 rounded-lg p-1 border border-dark-700/50">
          <button onClick={() => setView('grouped')}
            className={`text-xs px-3 py-1 rounded ${view === 'grouped' ? 'bg-accent-primary/20 text-accent-primary' : 'text-gray-400 hover:text-white'}`}>
            By admin
          </button>
          <button onClick={() => setView('flat')}
            className={`text-xs px-3 py-1 rounded ${view === 'flat' ? 'bg-accent-primary/20 text-accent-primary' : 'text-gray-400 hover:text-white'}`}>
            All sessions
          </button>
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name / email / role…"
            className="w-full bg-dark-800 border border-dark-700/50 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder-gray-600 focus:border-accent-primary focus:outline-none" />
        </div>
      </div>

      {/* Recent revokes — inline so the operator doesn't have to bounce to Audit Log */}
      {recentRevokes.length > 0 && (
        <details className="bg-dark-800 rounded-xl border border-dark-700/50 p-3">
          <summary className="text-[11px] text-gray-400 cursor-pointer hover:text-white">
            Recent revokes — {recentRevokes.length}
          </summary>
          <div className="space-y-1 mt-2">
            {recentRevokes.map(e => (
              <div key={e.id} className="flex items-center gap-2 text-[10px] text-gray-500 px-2 py-1 rounded bg-dark-700/30">
                <Clock className="w-2.5 h-2.5" />
                <span>{new Date(e.timestamp).toLocaleString()}</span>
                <span>·</span>
                <span className="text-gray-300">{e.actorEmail}</span>
                <span>revoked</span>
                <span className="text-amber-300">{e.details?.targetEmail || '—'}</span>
                <span className="text-gray-500">({e.details?.revokedCount || 1} session{e.details?.revokedCount === 1 ? '' : 's'})</span>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Empty state when no admins at all */}
      {filteredAdmins.length === 0 ? (
        <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-8 text-center">
          <Lock className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-400">{search ? 'No admins match your search.' : 'No admin sessions to show.'}</p>
          {!search && (
            <p className="text-xs text-gray-600 mt-1">Invite team members from the Team tab to see their sessions here.</p>
          )}
        </div>
      ) : view === 'flat' ? (
        // FLAT: every session in one chronologically-sorted list
        flatSessions.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-12">No active sessions for these admins.</p>
        ) : (
          <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-4 space-y-1.5">
            {flatSessions.map(s => (
              <SessionRow key={s.jti} session={s} admin={s.admin}
                isThisDevice={s.jti === currentJti}
                onRevoke={s.admin.isOwner ? null : () => revokeOne(s.admin, s.jti)} />
            ))}
          </div>
        )
      ) : (
        // GROUPED: original per-admin cards (cleaned up)
        filteredAdmins.map(admin => (
          <div key={admin.userId} className="bg-dark-800 rounded-2xl border border-dark-700/50 p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-white truncate">{admin.name || admin.email}</p>
                  {admin.isOwner && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">OWNER</span>}
                  {!admin.mfaEnabled && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">MFA OFF</span>}
                </div>
                <p className="text-[11px] text-gray-500 truncate">{admin.email} · {admin.roles.join(', ') || 'no roles'}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-gray-500">{admin.activeSessionCount} active</span>
                {!admin.isOwner && admin.activeSessionCount > 0 && (
                  <button onClick={() => revokeAll(admin)} className="text-red-400 hover:text-red-300 text-[11px] px-2 py-1 rounded hover:bg-red-500/10">
                    Force-logout all
                  </button>
                )}
              </div>
            </div>

            {admin.sessions.length === 0 ? (
              <p className="text-[11px] text-gray-600 px-2">No active sessions.</p>
            ) : (
              <div className="space-y-1.5">
                {admin.sessions.map(s => (
                  <SessionRow key={s.jti} session={s}
                    isThisDevice={s.jti === currentJti}
                    onRevoke={admin.isOwner ? null : () => revokeOne(admin, s.jti)} />
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// ─── Tab: Approvals (4-eyes workflow) — premium edition ─────────────────────

const APPROVAL_STATUS_META = {
  pending:  { label: 'Pending',  color: 'bg-amber-500/15 text-amber-400 border-amber-500/30',     dot: 'bg-amber-400',  iconColor: 'text-amber-400' },
  executed: { label: 'Executed', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', dot: 'bg-emerald-400', iconColor: 'text-emerald-400' },
  rejected: { label: 'Rejected', color: 'bg-red-500/15 text-red-400 border-red-500/30',           dot: 'bg-red-400',    iconColor: 'text-red-400' },
  expired:  { label: 'Expired',  color: 'bg-gray-500/15 text-gray-400 border-gray-500/30',         dot: 'bg-gray-500',   iconColor: 'text-gray-400' },
  failed:   { label: 'Failed',   color: 'bg-red-500/15 text-red-400 border-red-500/30',           dot: 'bg-red-400',    iconColor: 'text-red-400' },
};

const SEVERITY_META = {
  destructive: { label: '🚨 Destructive', accent: 'border-red-500/30',     stripe: 'bg-red-500',     iconBg: 'bg-red-500/15 text-red-400',      blurb: "Cannot be undone — review carefully" },
  high:        { label: '⚠️ High-blast',  accent: 'border-amber-500/30',   stripe: 'bg-amber-500',   iconBg: 'bg-amber-500/15 text-amber-400',  blurb: "Affects platform behaviour or access" },
  standard:    { label: 'Standard',       accent: 'border-dark-700/50',    stripe: 'bg-gray-500',    iconBg: 'bg-accent-primary/15 text-accent-primary', blurb: "Routine admin action" },
};

function formatDuration(ms) {
  if (ms == null) return '—';
  if (ms < 60_000)        return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000)     return `${Math.round(ms / 60_000)}m`;
  if (ms < 86_400_000)    return `${Math.round(ms / 3_600_000)}h`;
  return `${Math.round(ms / 86_400_000)}d`;
}

function ApprovalCard({ approval, currentUserId, isOwner, currentPermissions, actionMeta, onDecide, busy }) {
  const [notes, setNotes] = useState('');
  const [showDecide, setShowDecide] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const statusMeta = APPROVAL_STATUS_META[approval.status] || APPROVAL_STATUS_META.pending;
  const meta = actionMeta?.[approval.action] || { severity: 'standard', requiresPermission: null };
  const sev = SEVERITY_META[meta.severity] || SEVERITY_META.standard;
  const isOwnRequest = approval.requestedByUserId === currentUserId;

  // Can the current admin approve this? Owner can always; otherwise must
  // not be the requester AND must hold the action's required permission.
  const hasReqPermission = !meta.requiresPermission
    || isOwner
    || (currentPermissions || []).includes(meta.requiresPermission);
  const canApprove = approval.status === 'pending' && (!isOwnRequest || isOwner) && hasReqPermission;
  const canReject  = approval.status === 'pending';

  // Time-left progress: 0–100 with three colour zones to convey urgency.
  const totalMs = new Date(approval.expiresAt).getTime() - new Date(approval.requestedAt).getTime();
  const remainingMs = Math.max(0, new Date(approval.expiresAt).getTime() - Date.now());
  const elapsedPct = totalMs > 0 ? Math.min(100, 100 - (remainingMs / totalMs * 100)) : 0;
  const timeLeftColor = remainingMs > 12 * 3600_000 ? 'bg-emerald-500'
                      : remainingMs > 6  * 3600_000 ? 'bg-amber-500'
                      : 'bg-red-500';
  const timeLeftLabel = remainingMs <= 0 ? 'expired' : `${formatDuration(remainingMs)} left`;

  return (
    <div className={`bg-dark-800 rounded-2xl border ${sev.accent} overflow-hidden`}>
      {/* Severity stripe down the left edge */}
      <div className="flex">
        <div className={`w-1 flex-shrink-0 ${sev.stripe}`} />
        <div className="flex-1 p-5">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className={`w-10 h-10 rounded-xl ${sev.iconBg} flex items-center justify-center flex-shrink-0`}>
                <Shield className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-white truncate">{approval.actionLabel}</p>
                  <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-dark-700/60 text-gray-400">{sev.label}</span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5 break-words">{approval.summary}</p>
                <p className="text-[11px] text-gray-500 mt-1.5">
                  Requested by <span className="text-gray-300">{approval.requestedByEmail || approval.requestedByUserId}</span>
                  {' · '}{new Date(approval.requestedAt).toLocaleString()}
                </p>
                {approval.decidedByEmail && (
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    {approval.status === 'executed' || approval.status === 'failed' ? 'Approved' : 'Decided'} by{' '}
                    <span className="text-gray-300">{approval.decidedByEmail}</span>
                    {' · '}{new Date(approval.decidedAt).toLocaleString()}
                    {approval.decisionNotes && <> · "{approval.decisionNotes}"</>}
                  </p>
                )}
                {approval.executionResult?.error && (
                  <div className="flex items-start gap-2 mt-2 p-2 rounded-lg bg-red-500/10 border border-red-500/20">
                    <AlertTriangle className="w-3 h-3 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] text-red-300">Execution failed: {approval.executionResult.error}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${statusMeta.color}`}>
                {statusMeta.label}
              </span>
              {approval.status === 'pending' && (
                <span className={`text-[10px] font-mono ${remainingMs <= 6 * 3600_000 ? 'text-red-400' : 'text-gray-500'}`}>
                  {timeLeftLabel}
                </span>
              )}
            </div>
          </div>

          {/* Time-left progress bar */}
          {approval.status === 'pending' && (
            <div className="h-1 bg-dark-700 rounded-full overflow-hidden mb-3">
              <div className={`h-full ${timeLeftColor} transition-all duration-500`} style={{ width: `${elapsedPct}%` }} />
            </div>
          )}

          {/* Expandable params */}
          {approval.params && Object.keys(approval.params).length > 0 && (
            <button onClick={() => setShowDetails(v => !v)}
              className="text-[11px] text-gray-500 hover:text-gray-300 flex items-center gap-1 mb-2">
              {showDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {showDetails ? 'Hide' : 'View'} action parameters
            </button>
          )}
          {showDetails && (
            <pre className="text-[10px] text-gray-400 bg-dark-900/60 border border-dark-700/50 rounded-lg p-3 mb-3 overflow-x-auto font-mono whitespace-pre-wrap break-all">
              {JSON.stringify(approval.params, null, 2)}
            </pre>
          )}

          {/* Decide controls */}
          {approval.status === 'pending' && !showDecide && (
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => setShowDecide(true)} disabled={!canApprove && !canReject}
                className="btn-primary text-xs flex items-center gap-1.5">
                {canApprove ? 'Review & decide' : 'Add note & cancel'}
              </button>
              {isOwnRequest && !isOwner && (
                <span className="text-[11px] text-gray-500">You can't approve your own request — another admin must decide.</span>
              )}
              {!isOwnRequest && !hasReqPermission && meta.requiresPermission && (
                <span className="text-[11px] text-amber-400">Requires <code className="text-amber-300">{meta.requiresPermission}</code> permission — you can reject but not approve.</span>
              )}
            </div>
          )}

          {showDecide && approval.status === 'pending' && (
            <div className="space-y-2 pt-3 border-t border-dark-700/30">
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional decision notes…"
                rows={2}
                className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-xs text-white placeholder-gray-600 focus:border-amber-500 focus:outline-none resize-none" />
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setShowDecide(false)} disabled={busy} className="btn-ghost text-xs">Cancel</button>
                {canReject && (
                  <button onClick={() => onDecide(approval, 'reject', notes)} disabled={busy}
                    className="text-xs px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 flex items-center gap-1.5">
                    <X className="w-3 h-3" /> Reject
                  </button>
                )}
                {canApprove && (
                  <button onClick={() => onDecide(approval, 'approve', notes)} disabled={busy}
                    className="btn-primary text-xs flex items-center gap-1.5">
                    <Check className="w-3 h-3" /> Approve + execute
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ApprovalsTab({ onToast }) {
  const { user } = useAuth();
  const { runWithStepUp } = useStepUpGate();
  const [data, setData] = useState({ approvals: [], counts: {}, pendingCount: 0, bypassActive: false, kpis: {}, distinctActions: [], actionMeta: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [actionFilter, setActionFilter] = useState('');
  const [busy, setBusy] = useState(false);

  const isOwner = user?.role === 'admin' || (Array.isArray(user?.roles) && user.roles.includes('owner'));
  const currentPermissions = Array.isArray(user?.permissions) ? user.permissions : [];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const status = filter === 'all' ? null : filter;
      const result = await listApprovals(status, actionFilter || null);
      setData(result);
    } catch (e) { onToast({ type: 'error', text: e.message }); }
    finally { setLoading(false); }
  }, [filter, actionFilter, onToast]);

  useEffect(() => { load(); }, [load]);

  const decide = async (approval, decision, notes) => {
    setBusy(true);
    try {
      await runWithStepUp(
        () => decideApproval(approval.id, decision, notes),
        `${decision === 'approve' ? 'Approve' : 'Reject'}: ${approval.summary}`
      );
      onToast({ type: 'success', text: decision === 'approve' ? 'Approved + executed' : 'Rejected' });
      load();
    } catch (e) { onToast({ type: 'error', text: e.message }); }
    finally { setBusy(false); }
  };

  const counts = data.counts || {};
  const kpis = data.kpis || {};

  return (
    <div className="space-y-5">
      {/* Hero banner */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-500/5 border border-amber-500/15 rounded-2xl">
        <div className="flex items-center gap-3 min-w-0">
          <Shield className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-gray-400 text-xs leading-relaxed truncate">
            <span className="text-amber-400 font-semibold">4-eyes approvals — </span>
            High-blast admin actions require a second admin with the right permission before they execute.
            {data.bypassActive && <span className="ml-2 text-red-400 font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-red-500/20 border border-red-500/40">⚠ Bypass active</span>}
          </p>
        </div>
        <button onClick={load} className="btn-ghost p-1.5"><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Pending right now" icon={Clock} iconBg="bg-amber-500/10" iconColor="text-amber-400"
          accent={counts.pending > 0 ? 'text-amber-400' : 'text-white'}
          value={counts.pending ?? 0}
          sub={counts.pending > 0 ? 'awaiting a second admin' : 'all clear'} />
        <KpiCard label="Decided today" icon={Check} iconBg="bg-emerald-500/10" iconColor="text-emerald-400"
          accent="text-emerald-400" value={kpis.decidedToday ?? 0}
          sub="since 00:00 UTC" />
        <KpiCard label="Approval rate (30d)" icon={TrendingUp} iconBg="bg-blue-500/10" iconColor="text-blue-400"
          accent="text-blue-400"
          value={kpis.approvalRate != null ? `${kpis.approvalRate}%` : '—'}
          sub={`across ${kpis.recentlyDecidedCount ?? 0} decisions`} />
        <KpiCard label="Avg decision time" icon={Activity} iconBg="bg-violet-500/10" iconColor="text-violet-400"
          accent="text-violet-400"
          value={formatDuration(kpis.avgDecisionMs)}
          sub="request → decision, 30d" />
      </div>

      {/* Filter row — status pills + action dropdown */}
      <div className="flex flex-wrap items-center gap-2">
        {['pending', 'executed', 'rejected', 'expired', 'failed', 'all'].map(s => {
          const count = s === 'all'
            ? Object.values(counts).reduce((a, b) => a + b, 0)
            : counts[s] || 0;
          const active = filter === s;
          return (
            <button key={s} onClick={() => setFilter(s)}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors flex items-center gap-1.5 ${
                active
                  ? 'bg-accent-primary/15 border-accent-primary/40 text-accent-primary'
                  : 'bg-dark-800 border-dark-700/50 text-gray-400 hover:text-white'
              }`}>
              {s === 'all' ? 'All' : APPROVAL_STATUS_META[s]?.label || s}
              <span className={`text-[10px] font-bold rounded-full px-1.5 min-w-[18px] inline-flex items-center justify-center ${
                active ? 'bg-accent-primary/30 text-accent-primary' :
                s === 'pending' && count > 0 ? 'bg-amber-500 text-dark-900' :
                'bg-dark-700 text-gray-400'
              }`}>{count}</span>
            </button>
          );
        })}
        {data.distinctActions?.length > 0 && (
          <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
            className="ml-auto text-xs bg-dark-800 border border-dark-700/50 text-gray-300 rounded-lg px-3 py-1.5 focus:border-accent-primary focus:outline-none">
            <option value="">All actions</option>
            {data.distinctActions.map(a => (
              <option key={a} value={a}>{data.actionMeta?.[a]?.label || a}</option>
            ))}
          </select>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-16"><Spinner className="text-accent-primary" /></div>
      ) : data.approvals.length === 0 ? (
        <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-10 text-center">
          {filter === 'pending' ? (
            <>
              <Check className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-sm text-gray-300 font-medium">All clear — no pending approvals</p>
              <p className="text-xs text-gray-500 mt-1">When an admin triggers a 4-eyes-gated action, the request appears here for a second admin to review.</p>
            </>
          ) : (
            <>
              <Shield className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No {filter} approvals{actionFilter ? ` for ${data.actionMeta?.[actionFilter]?.label || actionFilter}` : ''}.</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {data.approvals.map(a => (
            <ApprovalCard key={a.id} approval={a} currentUserId={user?.userId}
              isOwner={isOwner} currentPermissions={currentPermissions}
              actionMeta={data.actionMeta}
              onDecide={decide} busy={busy} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main AdminPage ────────────────────────────────────────────────────────────

// Each tab declares the minimum permission required to view it. `permission: null`
// means Owner-only (the tab's content uses Owner-only endpoints). Non-Owner
// role-holders see only the tabs they hold the corresponding permission for.
const TABS = [
  { id: 'command',  label: 'Command Center', icon: Crown,      permission: null },
  { id: 'business', label: 'Business',       icon: DollarSign, permission: 'business.read' },
  { id: 'users',    label: 'Users',          icon: Users,      permission: 'users.read' },
  { id: 'team',      label: 'Team',          icon: Shield,     permission: null }, // Owner-only
  { id: 'sessions',  label: 'Sessions',      icon: Activity,   permission: null }, // Owner-only
  { id: 'approvals', label: 'Approvals',     icon: Check,      permission: 'audit.read' },
  { id: 'audit',     label: 'Audit Log',     icon: ScrollText, permission: 'audit.read' },
  { id: 'config',   label: 'Platform Config',icon: Settings2,  permission: 'config.read' },
  { id: 'stream',   label: 'Event Stream',   icon: Radio,      permission: 'events.read' },
  { id: 'storage',  label: 'Storage',        icon: HardDrive,  permission: null }, // Owner-only (purge is destructive)
  { id: 'ads',      label: 'Ad Management',  icon: Megaphone,  permission: 'ads.read' },
  { id: 'paid-ch',  label: 'Paid Channels',  icon: Lock,       permission: 'paid_channels.read' },
];

export default function AdminPage() {
  return (
    <StepUpProvider>
      <AdminPageInner />
    </StepUpProvider>
  );
}

function AdminPageInner() {
  const { user } = useAuth();
  const [toast,     setToast]     = useState(null);
  const [botIds,    setBotIds]    = useState([]);

  // Owner = legacy admin role OR explicit 'owner' in user.roles.
  const isOwner = user?.role === 'admin' || (Array.isArray(user?.roles) && user.roles.includes('owner'));
  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];

  // Filter tabs to only those the current user can use.
  const visibleTabs = TABS.filter(t => {
    if (isOwner) return true;
    if (t.permission === null) return false; // Owner-only tabs
    return permissions.includes(t.permission);
  });

  const [activeTab, setActiveTab] = useState(visibleTabs[0]?.id || 'command');
  useEffect(() => {
    // If the user's permissions change and the active tab is no longer visible,
    // fall back to the first tab they can see.
    if (!visibleTabs.find(t => t.id === activeTab) && visibleTabs[0]) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  if (visibleTabs.length === 0) {
    return (
      <div className="max-w-md mx-auto mt-24 bg-dark-800 rounded-2xl p-8 border border-dark-700/50 text-center">
        <Shield className="w-10 h-10 text-amber-400 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-white">No admin permissions</h2>
        <p className="text-sm text-gray-400 mt-2">Your account doesn't have any admin roles assigned yet. Ask the platform Owner to grant you a role.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">

      {/* Page header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent-primary/15 text-accent-primary flex items-center justify-center">
            <Crown className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-accent-primary">Admin Center</h1>
            <p className="text-gray-400 text-sm mt-0.5">
              {isOwner ? 'Owner · Full platform visibility and control' : `Acting as ${(user?.roles || []).join(', ')}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <Shield className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-amber-400 text-xs font-medium">{isOwner ? 'Restricted Access' : 'Role-Limited Access'}</span>
        </div>
      </div>

      {/* Tab navigation — only tabs this user can use */}
      <div className="flex gap-1 bg-dark-800 p-1 rounded-xl border border-dark-700/50 overflow-x-auto">
        {visibleTabs.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-150 flex-1 justify-center ${
              activeTab === id ? 'bg-accent-primary text-dark-900' : 'text-gray-400 hover:text-white hover:bg-dark-700/60'
            }`}>
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'command' && <CommandCenterTab  onToast={showToast} />}
        {activeTab === 'business'&& <BusinessTab       onToast={showToast} />}
        {activeTab === 'users'   && <UsersTab          showToast={showToast} />}
        {activeTab === 'team'    && <TeamAdminTab      onToast={showToast} />}
        {activeTab === 'sessions'&& <AdminSessionsTab  onToast={showToast} />}
        {activeTab === 'approvals'&& <ApprovalsTab     onToast={showToast} />}
        {activeTab === 'audit'   && <AuditLogTab />}
        {activeTab === 'config'  && <PlatformConfigTab onToast={showToast} />}
        {activeTab === 'stream'  && <EventStreamTab    botIds={botIds}     />}
        {activeTab === 'storage' && <StorageTab        onToast={showToast} />}
        {activeTab === 'ads'     && <AdManagementTab   onToast={showToast} />}
        {activeTab === 'paid-ch' && <PaidChannelsAdminTab onToast={showToast} />}
      </div>

      <Toast message={toast} onClose={() => setToast(null)} />
    </div>
  );
}
